import {
  type ChangeEvent,
  type FormEvent,
  type DragEvent as ReactDragEvent,
  type ReactElement,
  Suspense,
  lazy,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useSyncExternalStore,
} from "react";

import type { PanelId } from "@dxf-ai-editor/contracts";
import {
  coerceColor,
  exportDrawingDocumentToDxfString,
  type DrawingDocument,
} from "@dxf-ai-editor/drawing-model";

import { Scene3DStage, type StageCameraApi } from "./scene3d";
// B8 — Dynamic Input overlay (sibling of Scene3DStage; reads camera /
// cursor via module-level snapshots).
import { DynInputOverlay } from "./scene3d/dynInput/DynInputOverlay";
import { GripArmedPrompt } from "./scene3d/grips/GripArmedPrompt";
// fix/grip-armed-prompt-dedup — read the SAME hot-grip store `GripArmedPrompt`
// subscribes to, so the pre-existing `.command-grip-mode-echo` strip below can
// be gated off while `GripArmedPrompt` is showing its own (richer) armed-grip
// status. See the `gripModeEchoText` status div for the full rationale.
import {
  getArmedGripSnapshot,
  subscribeHotGrips,
} from "./scene3d/grips/hotGripStore";
import { applyUrlRendererOverride } from "./state/rendererPreference";
import { emitSessionRendererTelemetry } from "./state/rendererTelemetry";
import { detectRendererBackendCapability } from "./state/detectRendererBackend";
import {
  buildAcceptedInputText,
  buildCommandHint,
  buildDraftPreviewEntity,
  buildDraftPrompt,
  buildStageStatusText,
  startDraftSession,
  type DraftCommandId,
  type DraftPoint,
  type DraftSession,
} from "./scene/draftingHelpers";
// W2.5 OSNAP-OVERRIDE-MODES-S2 — single source of truth for "does this
// DraftStep accept a point pick" (osnap-override draft-session-lifecycle
// clear + the Shift+right-click menu gate in useStageContextMenu.ts both
// reuse this same registry field rather than re-deriving a second,
// driftable classification).
import { getDraftStepDescriptor } from "./scene/drafting";
// Point-filter / temporary-tracking (FROM/M2P/.X.Y.Z — ТЗ §8.6/§10.4): DI's
// reference point must re-anchor onto a captured filter sub-point, mirroring
// Scene3DStage's ORTHO anchor (Codex PR #504 P2).
import { draftReferencePointWithFilter } from "./scene/pointFilterSession";
import type {
  BlockDefinition as BlockDefinitionRuntime,
  BlockInsertEntity as BlockInsertEntityRuntime,
} from "@dxf-ai-editor/drawing-model";
import { BLOCK_EDIT_SPACE_ID } from "@dxf-ai-editor/drawing-model";
import {
  derivePropertiesView,
  getEntityMetadataIndex,
  // Codex round 2 #8 (PR #87) + PR1b Codex P2 (iter-3) — cache-aware export.
  // Streaming OPEN commits a `geometryDocument` whose `space.entities[]` arrays
  // are empty (live entities live only in the compact cache). The per-space
  // snapshot build + warn-only audit now route through the shared store action
  // `auditExportReferences()` (see `buildExportedDxfText` below), so this file
  // no longer needs `peekDocumentMemoryHolder` / `snapshotSpaceEntities`
  // directly.
  selectActiveSpace,
  selectEntitiesInActiveSpace,
  selectMatchpropSourceEntity,
  selectModelSpaceEntities,
  selectSelectedEntities,
  useEditorStore,
} from "./state/editorStore";
import {
  applyLiveCeColor,
  applyLiveHeaderSysvars,
  buildCecolorHydration,
  buildHeaderSysvarHydration,
} from "./state/dxfGeometry";
import {
  getActiveDocumentId,
  peekStreamedSpaceIds,
} from "./state/mutationStore";
import { capDxfPreviewText } from "./state/dxfPreviewCap";
import { useStageHoverStore as stageHoverStore } from "./state/hoverState";
import { installPreviewSysvarBridge } from "./state/previewSysvarBridge";
import { installUndolimitSysvarBridge } from "./state/undolimitSysvarBridge";
import { installSysvarToggleTelemetryBridge } from "./state/sysvarToggleTelemetryBridge";
import {
  buildGripTransformCommand,
  type GripTransformEditMode,
} from "./state/gripModeTransform";
import { useT } from "./i18n";
import { ToolTipCard } from "./components/ToolTipCard";
// Phase 2.4 (Plan §8, 2026-05-20) — DimStyle + Multileader editors
// are the two biggest dialog components (~1.4 K + ~1 K LOC each).
// Lazy-load via `React.lazy` so their JS chunks ship separately and
// the initial bundle drops by their combined cost. Both dialogs are
// gated by `useState`-driven `isOpen` flags so the lazy chunk only
// fetches when the user actually opens the dialog.
const DimStyleEditorDialog = lazy(() =>
  import("./components/DimStyleEditorDialog").then((m) => ({
    default: m.DimStyleEditorDialog,
  })),
);
import { ImportProgressDialog } from "./components/ImportProgressDialog";
const MultileaderStyleEditorDialog = lazy(() =>
  import("./components/MultileaderStyleEditorDialog").then((m) => ({
    default: m.MultileaderStyleEditorDialog,
  })),
);
// Phase 2.4 batch 3 (Plan §8, 2026-05-20) — lazy-load 8 more dialogs.
const TextStyleEditorDialog = lazy(() =>
  import("./components/TextStyleEditorDialog").then((m) => ({
    default: m.TextStyleEditorDialog,
  })),
);
const FontManagerDialog = lazy(() =>
  import("./components/FontManagerDialog").then((m) => ({
    default: m.FontManagerDialog,
  })),
);
// Phase 2.4 batch 2 (Plan §8, 2026-05-20) — lazy-load LayerManager.
const LayerManagerDialog = lazy(() =>
  import("./components/LayerManagerDialog").then((m) => ({
    default: m.LayerManagerDialog,
  })),
);
import { hydrateFontsFromStore, IndexedDbFontStore } from "./fonts";
const BlockDefinitionDialog = lazy(() =>
  import("./components/BlockDefinitionDialog").then((m) => ({
    default: m.BlockDefinitionDialog,
  })),
);
const InsertBlockDialog = lazy(() =>
  import("./components/InsertBlockDialog").then((m) => ({
    default: m.InsertBlockDialog,
  })),
);
const OverlapWarningDialog = lazy(() =>
  import("./components/OverlapWarningDialog").then((m) => ({
    default: m.OverlapWarningDialog,
  })),
);
const BlockEditExitDialog = lazy(() =>
  import("./components/BlockEditExitDialog").then((m) => ({
    default: m.BlockEditExitDialog,
  })),
);
// Phase 2.4 batch 6 (Plan §8, 2026-05-20) — lazy-load
// ScaleListEditDialog. Dialog has internal `open` gate (always
// mounted in the prior version), but lazy + external isOpen wrap
// keeps the chunk off the initial bundle until SCALELISTEDIT fires.
const ScaleListEditDialog = lazy(() =>
  import("./components/ScaleListEditDialog").then((m) => ({
    default: m.ScaleListEditDialog,
  })),
);
const AnnotativeObjectScaleDialog = lazy(() =>
  import("./components/AnnotativeObjectScaleDialog").then((m) => ({
    default: m.AnnotativeObjectScaleDialog,
  })),
);
// Phase 2.4 batch 4 (Plan §8, 2026-05-20) — lazy-load WriteBlockDialog.
// Keep `WriteBlockParams` as a type-only import (erases at compile).
import type { WriteBlockParams } from "./components/WriteBlockDialog";
const WriteBlockDialog = lazy(() =>
  import("./components/WriteBlockDialog").then((m) => ({
    default: m.WriteBlockDialog,
  })),
);
const PurgeBlocksDialog = lazy(() =>
  import("./components/PurgeBlocksDialog").then((m) => ({
    default: m.PurgeBlocksDialog,
  })),
);
const RenameBlockDialog = lazy(() =>
  import("./components/RenameBlockDialog").then((m) => ({
    default: m.RenameBlockDialog,
  })),
);
const XrefManagerDialog = lazy(() =>
  import("./components/XrefManagerDialog").then((m) => ({
    default: m.XrefManagerDialog,
  })),
);
const AttributeExtractionDialog = lazy(() =>
  import("./components/AttributeExtractionDialog").then((m) => ({
    default: m.AttributeExtractionDialog,
  })),
);
const BlockAttributeManagerDialog = lazy(() =>
  import("./components/BlockAttributeManagerDialog").then((m) => ({
    default: m.BlockAttributeManagerDialog,
  })),
);
const BlockPropertiesManagerDialog = lazy(() =>
  import("./components/BlockPropertiesManagerDialog").then((m) => ({
    default: m.BlockPropertiesManagerDialog,
  })),
);
import {
  purgeBlocksMutation,
  validateRenameBlock as validateRenameBlockCore,
  wblockExportMutation,
  type PurgeCandidate,
} from "@dxf-ai-editor/command-engine";
const AttributeDefinitionDialog = lazy(() =>
  import("./components/AttributeDefinitionDialog").then((m) => ({
    default: m.AttributeDefinitionDialog,
  })),
);
const EditAttributesDialog = lazy(() =>
  import("./components/EditAttributesDialog").then((m) => ({
    default: m.EditAttributesDialog,
  })),
);
// Phase 2.4 batch 5 (Plan §8, 2026-05-20) — lazy-load
// EnhancedAttributeEditorDialog (~546 LOC). Mount is gated by
// `attributeDialogs.eattedit.isOpen` so the chunk only fetches on
// first EATTEDIT command invocation.
const EnhancedAttributeEditorDialog = lazy(() =>
  import("./components/EnhancedAttributeEditorDialog").then((m) => ({
    default: m.EnhancedAttributeEditorDialog,
  })),
);
const BlockAuthoringPalette = lazy(() =>
  import("./components/BlockAuthoringPalette").then((m) => ({
    default: m.BlockAuthoringPalette,
  })),
);
import { expandParameterSet } from "./components/BlockAuthoringPaletteParameterSets";
import {
  BlockAuthoringPaletteShell,
  type BlockAuthoringPlacementKind,
} from "./components/BlockAuthoringPaletteShell";
import { type BlockAuthoringPaletteTab } from "./components/BlockAuthoringPaletteShellTabs";
const BlockPropertiesTableDialog = lazy(() =>
  import("./components/BlockPropertiesTableDialog").then((m) => ({
    default: m.BlockPropertiesTableDialog,
  })),
);
const LookupTableDialog = lazy(() =>
  import("./components/LookupTableDialog").then((m) => ({
    default: m.LookupTableDialog,
  })),
);
const PrintPreviewDialog = lazy(() =>
  import("./components/PrintPreviewDialog").then((m) => ({
    default: m.PrintPreviewDialog,
  })),
);
const PageSetupManagerDialog = lazy(() =>
  import("./components/PageSetupManagerDialog").then((m) => ({
    default: m.PageSetupManagerDialog,
  })),
);
// PDF-6.9b — PDF Options sub-modal launched from the Page Setup Manager
// footer. Mounted inline next to the Page Setup mount; the lazy chunk
// keeps the PDF-specific options panel out of the main bundle.
const PdfOptionsDialog = lazy(() =>
  import("./components/PdfOptionsDialog").then((m) => ({
    default: m.PdfOptionsDialog,
  })),
);
import { BlockLookupMenu } from "./scene3d/grips/blockLookupMenu";
import {
  MleaderGripContextMenu,
  type MleaderGripKind,
  type MleaderMenuActionId,
} from "./scene3d/grips/mleaderGripContextMenu";
import {
  LINEWEIGHT_PRESET_VALUES,
  normalizeLineweightValue,
} from "./components/constants";
import {
  toTestIdSegment,
  formatCommandPoint,
  formatDraftNumber,
} from "./helpers/formatHelpers";
import {
  resolveShellDraftCommand,
  buildCommandSuggestions,
  createCommandLineInputKeyDownHandler,
} from "./hooks/useCommandLine";
import {
  DSettingsDialog,
  type DSettingsTab,
  useSysvarStore,
  useSysvarHotkeys,
} from "./dynamicInput";
import {
  useCeColor,
  useCeColorStore,
  DEFAULT_CE_COLOR,
} from "./state/cecolorStore";
import { AciColorPicker } from "./components/AciColorPicker";
import { CeColorSwatch } from "./components/CeColorSwatch";
import { createSubmitCommandText } from "./commandDispatch";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";
import {
  buildRibbonSections,
  ribbonTabIds,
  blockEditorRibbonTabIds,
  type RibbonTabId,
} from "./components/RibbonConfig";
import { EditorHeader } from "./components/EditorHeader";
import { RefeditToolbar } from "./components/RefeditToolbar";
import { StageContextMenu } from "./components/StageContextMenu";
// B12 — selection-cycling badge / dialog (AutoCAD SELECTIONCYCLING parity).
// Phase 2.4 batch 7 (Plan §8, 2026-05-20) — lazy-load
// SelectionCyclingDialog (~187 LOC). Mounted via internal
// `SelectionCyclingDialogMount` wrapper gated by store state.
const SelectionCyclingDialog = lazy(() =>
  import("./components/SelectionCyclingDialog").then((m) => ({
    default: m.SelectionCyclingDialog,
  })),
);
import { SelectionCyclingBadge } from "./scene3d/grips/selectionCyclingBadge";
import { useSelectionCyclingStore } from "./state/selectionCyclingState";
import { useDraftSession } from "./hooks/useDraftSession";
import { InspectorSidebar } from "./components/InspectorSidebar";
import { ViewportInspector } from "./components/ViewportInspector";
import { SessionShellCard } from "./components/SessionShellCard";
import { StatusStrips } from "./components/StatusStrips";
import { AppStyleEditorDialogs } from "./components/AppStyleEditorDialogs";
import { AppSpecialEditorDialogs } from "./components/AppSpecialEditorDialogs";
import { AppAttributeDialogs } from "./components/AppAttributeDialogs";
import { AppRemainingDialogs } from "./components/AppRemainingDialogs";
import { AppGripMenus } from "./components/AppGripMenus";
import { AppBlockAuthoringLayer } from "./components/AppBlockAuthoringLayer";
import { AppBlockEditOverlays } from "./components/AppBlockEditOverlays";
import { SpaceTabsBar } from "./components/SpaceTabsBar";
import { useSpaceContextStore } from "./state/spaceContextStore";
import { validateRenameSpace } from "./state/spaceMutationGuards";
import { useBackendBootstrap } from "./hooks/useBackendBootstrap";
import { useBlockDefinitionDialog } from "./hooks/useBlockDefinitionDialog";
import { useBlockEditExitDialog } from "./hooks/useBlockEditExitDialog";
import { useCommandFeed } from "./hooks/useCommandFeed";
import { useCoordinateSystem } from "./hooks/useCoordinateSystem";
import { useInspectorPanelState } from "./hooks/useInspectorPanelState";
import { useRibbonTabState } from "./hooks/useRibbonTabState";
import { useStageContextMenu } from "./hooks/useStageContextMenu";
import { useDimStyleEditorDialog } from "./hooks/useDimStyleEditorDialog";
import { useSpecialEditorHost } from "./hooks/useSpecialEditorHost";
import { useFontManagerDialog } from "./hooks/useFontManagerDialog";
import { useInsertBlockDialog } from "./hooks/useInsertBlockDialog";
import { useMLeaderStyleEditorDialog } from "./hooks/useMLeaderStyleEditorDialog";
import { useOverlapWarningDialog } from "./hooks/useOverlapWarningDialog";
import { useTextStyleEditorDialog } from "./hooks/useTextStyleEditorDialog";
import { useXrefDialog } from "./hooks/useXrefDialog";
import { usePrintDialog } from "./hooks/usePrintDialog";
import { usePageSetupDialog } from "./hooks/usePageSetupDialog";
import { usePdfOptionsDialog } from "./hooks/usePdfOptionsDialog";
import { useBpmDialog } from "./hooks/useBpmDialog";
import { useBattmanDialog } from "./hooks/useBattmanDialog";
import { useEattextDialog } from "./hooks/useEattextDialog";
import { useAttributeDialogs } from "./hooks/useAttributeDialogs";
import { useBlockAuthoringShell } from "./hooks/useBlockAuthoringShell";
import { useRefEditWorkflow } from "./hooks/useRefEditWorkflow";
import { useScaleListEditDialog } from "./hooks/useScaleListEditDialog";
import { useAnnotativeObjectScaleDialog } from "./hooks/useAnnotativeObjectScaleDialog";
import { ribbonIconMap } from "./components/RibbonIcons";

/**
 * PR-V4 — entity-list panel virtualization cap. The default render
 * budget for the right-rail "Объекты" panel; with no react-window in
 * the dependency tree we fall back to native scroll over a hard
 * upper-bound. Clicking the "show all" affordance below the list
 * bypasses the cap and renders every row (use cautiously on
 * 100k+-entity drawings — the DOM cost grows linearly).
 *
 * Set conservatively to 1_000 so the typical 30-entity selection-
 * inspection workflow still sees the full list while a 685k-entity
 * import does not freeze the inspector behind a multi-million-row
 * react reconciliation pass.
 */
const ENTITY_LIST_VIEW_CAP = 1_000;

const shellCommandExamples = [
  "LINE",
  "CIRCLE",
  "ARC",
  "PLINE",
  "RECTANG",
  "TEXT",
  "MTEXT",
  "HATCH",
  "BLOCK",
  "INSERT",
  "EXPLODE",
  "BEDIT",
  "BSAVE",
  "BCLOSE",
  "TESTBLOCK",
  "CLOSETESTBLOCK",
  "BPARAMETER",
  "BACTION",
  "BVSTATE",
  "BCPARAMETER",
  "BLOOKUPTABLE",
  "BTABLE",
  "BAUTHORPALETTE",
  "BAUTHORPALETTECLOSE",
  "BGRIPSET",
  "WBLOCK",
  "PURGE",
  "RENAME",
  "EATTEXT",
  "BCOUNT",
  "XATTACH",
  "XREF",
  "-XREF",
  "XBIND",
  "XUNLOAD",
  "XRELOAD",
  "XDETACH",
  "XCLIP",
  "XOPEN",
  "ATTDEF",
  "DDATTE",
  "ATTEDIT",
  "EATTEDIT",
  "BATTMAN",
  "BPM",
  "ATTSYNC",
  "ARRAYRECT",
  "DIMENSION",
  "MLEADER",
  "REVCLOUD",
  "MVIEW",
  "MODEL",
  "LAYOUT layout-1",
  "LAYOUT NEW Sheet2",
  "LAYOUT DELETE Sheet2",
  "MSPACE",
  "PSPACE",
  "CHPROP Layer=A-AXIS",
  "MATCHPROP SOURCE",
  "MATCHPROP ALL",
];

/**
 * ## Function dependency map
 *
 * App (component)
 *   orchestration
 *     dispatch() -> editorStore.dispatch (command routing)
 *     importDxfText() -> editorStore.importDxfText -> dxfGeometry bridge
 *     importDxfTextAsync() -> editorStore.importDxfTextAsync
 *                             -> progress dialog (ImportProgressDialog)
 *                             -> dxfGeometry progress bridge
 *     handleDxfFileChange() -> importDxfTextAsync
 *   drafting
 *     useDraftSession() -> beginDrafting / handleStagePoint / submitCommandText
 *     buildDraftPreviewEntity() / buildDraftPrompt() / buildStageStatusText()
 *   command line
 *     useCommandLine: resolveShellDraftCommand / buildCommandSuggestions
 *     handleCommandSubmit() -> beginDrafting / dispatch
 *   dialogs
 *     DimStyleEditorDialog -> updateDimensionStyle / addDimensionStyle
 *     MultileaderStyleEditorDialog -> updateMultileaderStyle / addMultileaderStyle
 *     TextStyleEditorDialog -> updateTextStyle
 *     LayerManagerDialog -> addLayer / updateLayer / deleteLayer / renameLayer / setCurrentLayer
 *   selectors
 *     selectActiveSpace / selectEntitiesInActiveSpace / selectSelectedEntities
 *     derivePropertiesView / selectMatchpropSourceEntity
 *   rendering
 *     <PrimitiveAtlasStage> / <EditorHeader> / <InspectorSidebar>
 *     <StatusStrips> / <SessionShellCard>
 *
 * ## Key state dependencies
 *
 * useState: commandText -> command suggestions, submission
 * useState: layerManagerOpen -> dialog visibility (DimStyle / MLeaderStyle /
 *   TextStyle / FontManager dialogs were moved into feature hooks in
 *   Track 3 Phase 3 — `useDimStyleEditorDialog`, `useMLeaderStyleEditorDialog`,
 *   `useTextStyleEditorDialog`, `useFontManagerDialog`).
 * useState: activeRibbonTab -> ribbon section rendering
 * useBackendBootstrap: backendRuntime -> EditorHeader, StatusStrips
 * useDraftSession: draft session -> preview entities, stage interaction
 * useCommandFeed: commandFeed -> transcript display
 */

export function App() {
  const t = useT();
  const state = useEditorStore((store) => store.state);
  const geometryDocument = useEditorStore((store) => store.geometryDocument);
  const dispatch = useEditorStore((store) => store.dispatch);
  // GRIPS-SUBSYSTEM (§5.11) — STABLE grip-drag callbacks. Codex P1 (#317):
  // inline arrow props were re-created on every render, so the first
  // pointermove's `properties/apply` re-render handed Scene3DStage new
  // callback identities mid-drag → the grip handler bundle rebuilt → its
  // unmount cleanup fired `abort` and killed the active drag on every frame.
  // `dispatch` is a stable Zustand selector, so these `useCallback`s keep a
  // constant identity across the whole drag (defence-in-depth alongside the
  // stable active-finish ref in Scene3DStage). Escape / lost-input aborts the
  // batch; pointerup commits it (one Ctrl+Z reverts the whole grip move).
  const handleGripDrag = useCallback(
    (entityId: string, values: Record<string, string>) => {
      dispatch({
        type: "properties/apply",
        source: "scene",
        entityIds: [entityId],
        values,
      });
    },
    [dispatch],
  );
  const handleGripDragStart = useCallback(() => {
    dispatch({
      type: "history/begin-batch",
      source: "scene",
      // GRIPS-INTERACTION PR-2 — dispatched at PRESS time, before the
      // eventual mode (possibly cycled away from Stretch) is known. Left
      // static rather than relabelled at commit: `history/commit-batch` has
      // no relabel affordance without new plumbing, and the label is
      // cosmetic-only (the undo entry's EFFECT is identical either way) —
      // not worth adding for a history-panel display string.
      label: "GRIP STRETCH",
    });
  }, [dispatch]);
  const handleGripDragEnd = useCallback(() => {
    dispatch({ type: "history/commit-batch", source: "scene" });
  }, [dispatch]);
  const handleGripDragCancel = useCallback(() => {
    dispatch({ type: "history/abort-batch", source: "scene" });
  }, [dispatch]);
  // GRIPS-INTERACTION PR-2 (Ctrl→Enter/Space correction, §8.2) — the armed
  // hot-grip modal's MOVE/ROTATE/SCALE/MIRROR commit (cycled via Enter/Space
  // — STRETCH stays on `handleGripDrag` above, unchanged). `entityIds` /
  // `basePoint` / `targetPoint` arrive as call arguments from
  // `StageGripDrag.ts`'s `selectedIdsRef.current` / armed-grip `.world` / the
  // resolved commit point — NOT from `state.selection.entityIds` — so this
  // stays `[dispatch]`-only, matching `handleGripDrag`'s own stable-callback
  // shape (Codex P1 #317: a non-stable identity here would rebuild
  // Scene3DStage's memoised grip bundle every render).
  const handleGripTransformCommit = useCallback(
    (
      mode: GripTransformEditMode,
      entityIds: readonly string[],
      basePoint: { x: number; y: number },
      targetPoint: { x: number; y: number },
    ) => {
      const command = buildGripTransformCommand(
        mode,
        entityIds,
        basePoint,
        targetPoint,
        "scene",
        // Live MIRRTEXT sysvar, threaded onto MIRROR's command the same way
        // `useDraftSession.ts`'s `dispatchWithMleaderEditGuards` stamps it
        // onto the click-based MIRROR command — captured at commit time via
        // `.getState()` (not a reactive subscription: this callback fires
        // later from an event handler, not during render).
        useSysvarStore.getState().values.MIRRTEXT,
      );
      // `buildGripTransformCommand` returns null only for an empty
      // `entityIds` (mirrors `translateGripDrag`'s null-for-no-op contract) —
      // skip the dispatch, same as `handleGripDrag`'s reducer-side no-op path
      // for an unrecognised grip.
      if (command) dispatch(command);
    },
    [dispatch],
  );
  // OPEN-style import (replaces current drawing). The legacy
  // `importDxfFileAsync` (merge / INSERT semantics) remains on the
  // store for a future INSERT UI affordance; the primary file picker
  // now uses the replacing variant per AutoCAD parity.
  const importDxfFileAsyncReplacing = useEditorStore(
    (store) => store.importDxfFileAsyncReplacing,
  );
  const hydrateSessionShell = useEditorStore(
    (store) => store.hydrateSessionShell,
  );
  // Track 3 Phase 3 — DimStyle / MLeaderStyle store bindings moved into
  // `useDimStyleEditorDialog` / `useMLeaderStyleEditorDialog`. The hooks
  // own `updateDimensionStyle / addDimensionStyle / updateMultileaderStyle
  // / addMultileaderStyle / deleteMultileaderStyle /
  // recordAnnotativeStylePropagation` directly.
  // Layer management — bound for the LayerManagerDialog.
  const addLayer = useEditorStore((store) => store.addLayer);
  const updateLayer = useEditorStore((store) => store.updateLayer);
  const deleteLayer = useEditorStore((store) => store.deleteLayer);
  const renameLayer = useEditorStore((store) => store.renameLayer);
  const setCurrentLayer = useEditorStore((store) => store.setCurrentLayer);
  // Note: TextStyleEditorDialog reads updateTextStyle from the store directly
  // (see line ~1315). No App-scope binding required.
  const reset = useEditorStore((store) => store.reset);
  // Phase 4.1.b — history slice selectors for Undo / Redo quickbar buttons.
  const historyPastLength = useEditorStore(
    (store) => store.state.history.past.length,
  );
  const historyFutureLength = useEditorStore(
    (store) => store.state.history.future.length,
  );
  // Phase 4.1.k iter-3 — batch depth selector for nested UNDO BEGIN/END semantics.
  const historyBatchDepth = useEditorStore(
    (store) => store.state.history.current?.depth ?? 0,
  );
  // PR-K (2026-05-05) — Three.js / WebGPU renderer became default.
  // The rendererPreference store is retained for forward compatibility
  // (PR-L will introduce additional renderer variants for 3D mode);
  // App no longer branches on the value because the legacy SVG path
  // (`PrimitiveAtlasStage`) was deleted in this PR.
  useEffect(() => {
    // PR-K (2026-05-05): URL override is gone (Q2 user decision —
    // one-way migration). The call is retained as a deprecated no-op
    // for one release so external code that imported the symbol keeps
    // resolving; remove after one release. The `editor.renderer`
    // value is sourced exclusively from persisted localStorage / the
    // hardcoded `"webgl"` default.
    applyUrlRendererOverride();
    // Q3.A telemetry: emit a single session.renderer counter once we
    // know the entity count after the first import settles. The
    // emission is idempotent (rendererTelemetry guards against
    // double-fire) so React StrictMode double-invokes are safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Q3.A — single-shot session.renderer telemetry. Fires once we have
  // a non-zero entity count (post first DXF import) so the metric
  // reflects real user content, not an empty seed canvas. The
  // `alreadyEmitted` latch inside `rendererTelemetry.ts` makes the
  // effect idempotent — repeat renders / scene resets are harmless.
  const importedEntityCount = useEditorStore(
    (store) => store.geometryDocument.spaces[0]?.entities.length ?? 0,
  );
  useEffect(() => {
    if (importedEntityCount === 0) return;
    emitSessionRendererTelemetry({
      renderer: detectRendererBackendCapability(),
      entityCountAtImport: importedEntityCount,
    });
  }, [importedEntityCount]);
  const dxfImportInputRef = useRef<HTMLInputElement | null>(null);
  const beginDraftingRef = useRef<
    (command: DraftCommandId, options?: Record<string, string>) => void
  >(() => undefined);
  const submitCommandTextRef = useRef<
    (rawText: string, source?: "user" | "batch") => boolean
  >(() => false);
  // D5 — moved up from its original position right before the (now
  // extracted) `submitCommandText` definition so `createSubmitCommandText`
  // can read it while building `deps` without a temporal-dead-zone hazard
  // (a `const` read in an eagerly-evaluated object literal, unlike the old
  // hoisted `function submitCommandText` declaration, is NOT hoist-safe).
  // See the AI_BATCH_COMMANDS comment further down for what this ref is for.
  const renderSignalResolversRef = useRef<Array<() => void>>([]);
  const stageCameraApiRef = useRef<StageCameraApi | null>(null);
  // cmd-pan — interactive realtime-pan session (bare PAN / right-click 'Pan').
  // The REF is read at event-time by the stage's LMB pan handler (no re-render
  // needed); the STATE drives the visible prompt strip + hand cursor. Both flip
  // together. PAN is a pure view-op — never through the geometry reducer.
  const panSessionActiveRef = useRef(false);
  const [panSessionActive, setPanSessionActive] = useState(false);
  // Coordinate system: "cad" (Y up, AutoCAD/DXF) or "svg" (Y down, legacy).
  // Stored in localStorage so user's preference persists across sessions.
  // Track 3 Phase 7 — owned by `useCoordinateSystem`.
  const { coordinateSystem, toggle: toggleCoordinateSystem } =
    useCoordinateSystem();

  // Install the SELECTIONPREVIEW / PREVIEWEFFECT / PREVIEWFILTER /
  // ROLLOVERTIPS sysvar→previewPreferences bridge so SETVAR writes
  // flow into the renderer + ToolTipCard. Installed once at mount;
  // teardown on unmount avoids HMR subscriber pile-up.
  useEffect(() => {
    const teardown = installPreviewSysvarBridge();
    return teardown;
  }, []);

  // Install the UNDOLIMIT sysvar→history.depthCap bridge so SETVAR
  // UNDOLIMIT writes immediately cap the past[] stack in editorStore.
  useEffect(() => {
    const teardown = installUndolimitSysvarBridge();
    return teardown;
  }, []);

  // CC-1e — install the drawing-aid toggle telemetry bridge so OSNAP / ORTHO /
  // POLAR / SNAP / GRID flips emit a typed `ui-toggle` telemetry event (ТЗ §14
  // "clicks" axis). Observability only — never mutates a sysvar.
  useEffect(() => {
    const teardown = installSysvarToggleTelemetryBridge();
    return teardown;
  }, []);

  // ABB-S7c — wire the agent-bridge export.dxf seam. The bridge's export.dxf
  // handler reads `window.__editDwgExportedDxfText` (a `() => string` getter,
  // agentBridgeMount.ts:readExportedDxfText). We install a STABLE DELEGATING
  // function (empty dep array — the ref identity is stable) that always calls
  // through to the LATEST `agentExportRef.current`. Capturing `.current`
  // directly at mount time would freeze a stale closure (the ref is reassigned
  // on every render with fresh document/sysvar state); delegating keeps the
  // seam returning the LIVE serialized DXF instead of undefined / a stale snapshot.
  //
  // The agent seam delegates to `agentExportRef` (the side-effect-FREE variant),
  // NOT `exportedDxfTextRef` (the user Ctrl+S / Download path). An agent read must
  // not push an "unresolved references" audit warning: that warning is user-export
  // semantics, and pushing it here would mutate the very audit ring the bridge's
  // own observers report back (self-contamination + possible 24-slot eviction).
  useEffect(() => {
    (window as unknown as Record<string, unknown>)["__editDwgExportedDxfText"] =
      () => agentExportRef.current();
    return () => {
      delete (window as unknown as Record<string, unknown>)[
        "__editDwgExportedDxfText"
      ];
    };
  }, []);

  // Phase MC1 — initialize SpaceContext store from geometryDocument.spaces
  // whenever the document changes (DXF import / load). Only "model" and
  // "layout" kinds are shown as tabs; block-edit / refedit spaces are hidden.
  const geometrySpaces = geometryDocument.spaces;
  const geometryDocumentId = geometryDocument.id;
  useEffect(() => {
    useSpaceContextStore.getState().initFromSpaces(geometryDocumentId, geometrySpaces);
  }, [geometryDocumentId, geometrySpaces]);

  // Phase MC1 — keep SpaceContext mode in sync with editorStore's
  // activeSpaceId / layoutContext (driven by CLI commands MSPACE/PSPACE/LAYOUT).
  const editorActiveSpaceId = state.drawing.activeSpaceId;
  const editorLayoutContext = state.drawing.layoutContext;
  useEffect(() => {
    useSpaceContextStore
      .getState()
      .syncFromEditor(editorActiveSpaceId, editorLayoutContext);
  }, [editorActiveSpaceId, editorLayoutContext]);

  // Hydrate the `FontRegistry` from IndexedDB on mount so SHX / TTF
  // fonts the user uploaded in a previous session are available before
  // the renderer reaches the first TEXT / MTEXT entity. Failures are
  // intentionally swallowed — the dialog surfaces the issue when the
  // user next opens the Font Manager, and the renderer falls back to
  // the system `<text>` element in the meantime.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const store = new IndexedDbFontStore();
        if (cancelled) return;
        await hydrateFontsFromStore(store);
      } catch {
        // IndexedDB unavailable (private mode / legacy browser / CSP) —
        // we continue without hydration. Uploads within this session
        // still work but won't persist across reloads.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const [commandText, setCommandText] = useState("");
  const [propertyDraft, setPropertyDraft] = useState<Record<string, string>>(
    {},
  );
  const [commandHistoryOpen, setCommandHistoryOpen] = useState(false);
  // B2 — inline rename for layout tabs. Non-null while the user is
  // editing a tab name; set by onDoubleClick on non-Model tabs and
  // cleared on blur / Enter / Escape. Model space is immutable
  // (AutoCAD parity) so double-click on it is a no-op.
  const [renamingSpaceId, setRenamingSpaceId] = useState<string | null>(null);
  const [renamingDraftName, setRenamingDraftName] = useState<string>("");
  const { commandFeed, appendCommandFeed, clearCommandFeed } = useCommandFeed();
  // GRIPS-INTERACTION PR-2 fix — `commandFeed` has ZERO JSX consumers (dead
  // state, confirmed by grep across the codebase), so routing the armed
  // hot-grip modal prompt through `appendCommandFeed` wrote text nobody ever
  // sees. Mirrors the PROVEN-VISIBLE `onZoomPromptChange`/`panSessionActive`
  // pattern instead: a dedicated local-state string, directly rendered near
  // the drawing stage (see the `gripModeEchoText` status div below).
  const [gripModeEchoText, setGripModeEchoText] = useState<string>("");
  const handleGripModeEcho = useCallback((text: string) => {
    setGripModeEchoText(text);
  }, []);
  // fix/grip-armed-prompt-dedup — regression from N4 GRIPS-ARMED-STRETCH-I18N
  // (#531): while a grip is click-armed, `GripArmedPrompt` (mode banner +
  // point-prompt, e.g. "** РАСТЯНУТЬ **" / "Укажите точку растяжения:") and
  // this `gripModeEchoText` strip (single-line "** РАСТЯНУТЬ **" only) both
  // rendered simultaneously — two overlapping command-line status surfaces,
  // which AutoCAD never shows at once. `GripArmedPrompt`'s banner line reuses
  // the SAME `grip.mode.*` key this echo shows (`gripInteractionConstants.ts`
  // `GRIP_ARMED_PROMPT_KEYS`), so it is a strict content superset of the
  // armed-case echo — nothing is lost by suppressing the echo strip while
  // armed. Independent third subscriber to `hotGripStore` (mirrors
  // `GripArmedPrompt`'s own doc comment: "a second independent subscriber to
  // the one authoritative armed-grip state — not a parallel tracking
  // mechanism"), so this can never drift out of sync with the condition that
  // actually gates `GripArmedPrompt`'s own render. Defense-in-depth, not the
  // sole guard: `useGripModeEcho.ts` was ALSO fixed (Codex review P2 finding
  // on this same change) to never set `gripModeEchoText` to non-empty
  // content while a grip is armed in the first place — that fix alone
  // already makes the condition below vacuously true whenever armed, since
  // there is nothing left to leak. Keeping BOTH means the render can never
  // show the armed-case echo even if a future edit to `useGripModeEcho.ts`
  // reintroduces a stray non-empty write during the armed window.
  const isGripArmedForEchoGate =
    useSyncExternalStore(
      subscribeHotGrips,
      getArmedGripSnapshot,
      getArmedGripSnapshot,
    ) !== null;
  // Track 3 Phase 7 — owned by `useInspectorPanelState`.
  const {
    isCollapsed: isInspectorCollapsed,
    expand: expandInspector,
    toggle: toggleInspectorCollapsed,
  } = useInspectorPanelState(false);
  const [stageResetToken, setStageResetToken] = useState(0);
  // DSettings (Drafting Settings) dialog — opened via DSETTINGS / DDRMODES
  // commands or right-click on the "ДИН" status bar button.
  const [dsettingsOpen, setDsettingsOpen] = useState(false);
  // W2.5 OSNAP-OVERRIDE-MODES-S2 — local Codex review (round-2) P2: the
  // Shift+right-click Object Snap menu's "Osnap Settings..." item must land
  // on the Object Snap tab, not `DSettingsDialog`'s own "dynamic-input"
  // default (which every OTHER opener — DSETTINGS/DDRMODES, the "ДИН"
  // status-bar button, `DynInputOverlay`'s settings gear — still wants
  // unchanged). Defaults to `"dynamic-input"` so every existing opener that
  // does not explicitly set this stays byte-identical.
  const [dsettingsInitialTab, setDsettingsInitialTab] =
    useState<DSettingsTab>("dynamic-input");
  // W2.5 — the SINGLE choke point every "open DSettings" caller goes
  // through, so a caller that landed the dialog on a specific tab (e.g. the
  // Osnap override menu → "osnap") can never leak that choice into a LATER,
  // unrelated opener that expects the plain default (DSETTINGS/DDRMODES
  // command text, the "ДИН" status-bar right-click, `onOpenDynInputSettings`,
  // `DynInputOverlay`'s settings gear — all four still want "dynamic-input",
  // their pre-existing behaviour before this slice introduced the tab
  // concept at all). Mirrors this file's other single-choke-point clearers
  // (`clearColorTransientState`, `wrapItemActionsWithPanSupersede`).
  const openDsettingsOnTab = useCallback((tab: DSettingsTab) => {
    setDsettingsInitialTab(tab);
    setDsettingsOpen(true);
  }, []);
  // CECOLOR — the "Select Color" dialog opened by COLOR/SETCOLOR/ЦВЕТ (no value)
  // and the ribbon swatch; `colorPromptPending` arms the `-COLOR` dialog-less
  // keyword prompt to consume the next command-line submission.
  const [ceColorPickerOpen, setCeColorPickerOpen] = useState(false);
  const [colorPromptPending, setColorPromptPending] = useState(false);
  // Track 3 Phase 3 — DimStyle / MLeaderStyle / TextStyle / FontManager
  // dialog state moved into feature hooks (`useDimStyleEditorDialog`,
  // `useMLeaderStyleEditorDialog`, `useTextStyleEditorDialog`,
  // `useFontManagerDialog`). The hooks are wired below — see
  // `dimStyleEditorDialog`, `mleaderStyleEditorDialog`,
  // `textStyleEditorDialog`, `fontManagerDialog`.
  const [layerManagerOpen, setLayerManagerOpen] = useState(false);
  // Right-click context menu — owned by `useStageContextMenu`
  // (Track 3 Phase 7). The hook is constructed below once
  // `submitCommandTextRef` and `draftSession` are available.
  // PR-V4 — entity-list panel virtualization. Default cap keeps DOM
  // small for huge imports; user clicks "show all" to bypass it.
  // Acts as the upper bound on per-frame DOM cards even when react-window
  // is not available in the dependency graph.
  const [entityListShowAll, setEntityListShowAll] = useState(false);
  // ── BLOCK / INSERT / Overlap dialog state ──
  // Track 3 Phase 2 — moved into feature hooks
  // (`useBlockDefinitionDialog`, `useInsertBlockDialog`,
  // `useOverlapWarningDialog`). The orchestrator wires them below
  // (after the draft-session is available) and consumes their
  // `{ isOpen, props, close, interceptOverlapForBlockInsert }` API.
  // Phase J.5 — BlockEditExitDialog state moved into
  // `useBlockEditExitDialog` (Track 3 Phase 1).
  // ── WBLOCK / PURGE / RENAME dialog state (Phase 2.6 / 2.7 / 2.8) ──
  // These three commands are stateless one-shot dialogs — they do not
  // participate in the drafting state machine. The commandline handler
  // (submitCommandText) sets the relevant open flag directly when the
  // user types WBLOCK / PURGE / RENAME; the dialog's Cancel handler
  // toggles the flag off.
  const [writeBlockDialogOpen, setWriteBlockDialogOpen] = useState(false);
  const [purgeBlocksDialogOpen, setPurgeBlocksDialogOpen] = useState(false);
  const [renameBlockDialogOpen, setRenameBlockDialogOpen] = useState(false);
  const [purgeCandidates, setPurgeCandidates] = useState<PurgeCandidate[]>([]);
  // ── PR-9 — SCALELISTEDIT / Annotative Object Scale dialogs ──
  // Owned by `useScaleListEditDialog` and `useAnnotativeObjectScaleDialog`
  // (Track 3 Phase 4). Both expose imperative open/close so the
  // command-line router and Properties palette button can drive them.
  // ── XREF Manager dialog state (Phase FIN.3) ──
  // Owned by `useXrefDialog` (Track 3 Phase 4). The hook also owns the
  // per-action handlers (XATTACH/XBIND/XOPEN/XEDIT/XCLIP all share the
  // single manager dialog).
  // ── B11 PRINT / PLOT dialog state ──
  // Owned by `usePrintDialog` (Track 3 Phase 4). The hook also owns the
  // `printPreviewWorldBounds` derivation that drives the canvas overlay
  // rectangle while the dialog is open.
  // ── B19 Block lookup grip menu state ──
  // Set by Scene3DStage's `onBlockLookupGripClick` callback; cleared
  // on outside-click / Esc / row-pick.
  const [blockLookupMenu, setBlockLookupMenu] = useState<{
    entityId: string;
    parameterId: string;
    screen: { x: number; y: number };
  } | null>(null);
  // ── B18 MLEADER grip context menu state ──
  // Set by Scene3DStage's `onMleaderGripContextMenu` callback; cleared
  // on Esc / outside-click / item pick.
  const [mleaderGripMenu, setMleaderGripMenu] = useState<{
    entityId: string;
    gripKind: MleaderGripKind;
    gripPayload: unknown;
    gripWorld: { x: number; y: number };
    screen: { x: number; y: number };
  } | null>(null);
  // ── EATTEXT / BATTMAN / BPM dialog state ──
  // Owned by `useEattextDialog` / `useBattmanDialog` / `useBpmDialog`
  // (Track 3 Phase 5). Each hook exposes imperative `open()` / `close()`
  // for the command-line router and `resetShellState()`, plus the props
  // bundle that the orchestrator spreads into the matching dialog.
  // ── ATTDEF / DDATTE / EATTEDIT dialog state (Phase 2.1 + 2.2) ──
  // Owned by `useAttributeDialogs` (Track 3 Phase 6). The hook owns
  // the three open flags, the auto-open mirror of `draftSession.step`,
  // the Confirm / Cancel / Apply handlers, and `closeAll()` for
  // `resetShellState()`.
  // ── DYN-1.11 Block Authoring Palette + Phase K.1 floating shell ──
  // Owned by `useBlockAuthoringShell` (Track 3 Phase 6). The hook
  // owns the palette visibility flag, the open-request signal
  // consumed by the DYN-1.7 palette (BPARAMETER / BACTION / BVSTATE /
  // BCPARAMETER), and the persisted shell state (open / position /
  // tab / dock).
  // DYN-2 B.2 — Lookup table / block properties table dialog state.
  // Both dialogs are keyed by block name so they survive across
  // dispatches without drifting off the active BEDIT session.
  const [lookupTableDialog, setLookupTableDialog] = useState<{
    blockName: string;
    tableId: string;
  } | null>(null);
  const [blockPropertiesDialogOpen, setBlockPropertiesDialogOpen] =
    useState(false);
  // Track 3 Phase 7 — owned by `useRibbonTabState`. The auto-switch
  // effect that previously lived inline (Phase J.3 BEDIT boundary) is
  // now encapsulated inside the hook.
  const { activeRibbonTab, setActiveRibbonTab } = useRibbonTabState({
    isInBlockEdit:
      geometryDocument.activeSpaceId === BLOCK_EDIT_SPACE_ID,
  });
  const [dxfImportStatus, setDxfImportStatus] = useState(() =>
    t("dxfio.status.default"),
  );
  const { backendRuntime, backendPersistentSlice } =
    useBackendBootstrap(hydrateSessionShell);

  // Track 3 Phase 2 — Overlap warning dialog hook. Owns the
  // pendingOverlapInsert state, the dispatch interceptor, and the
  // Continue / Cancel handlers. We instantiate it BEFORE
  // `useDraftSession` because the draft session needs the
  // interceptor; `setDraftSession` is consumed by the Continue /
  // Cancel handlers but those only fire on user click, by which
  // time `draftSessionRefForHooks.current` has been wired below.
  const draftSessionRefForHooks = useRef<
    ((session: DraftSession | null) => void) | null
  >(null);
  const stableSetDraftSession = useCallback(
    (session: DraftSession | null) => {
      draftSessionRefForHooks.current?.(session);
    },
    [],
  );
  const overlapWarningDialog = useOverlapWarningDialog({
    setDraftSession: stableSetDraftSession,
    setCommandText,
    appendCommandFeed,
  });

  const draft = useDraftSession({
    appendCommandFeed,
    setCommandText,
    setCommandHistoryOpen,
    formatCommandPoint,
    interceptGeometryDispatch:
      overlapWarningDialog.interceptOverlapForBlockInsert,
  });
  const {
    draftSession,
    setDraftSession,
    liveCanvasText,
    setLiveCanvasText,
    inPlaceEditState,
    setInPlaceEditState,
    zoomPickingPrompt,
    setZoomPickingPrompt,
    beginDrafting,
    handleDraftTextSubmission,
    handleStagePoint,
    handleStageHover,
    handleEntityDoubleClick,
    selectEntityFromStage,
    selectEntitiesFromStage,
  } = draft;
  // Wire the deferred setter so `overlapWarningDialog`'s handlers
  // (and the BLOCK / INSERT hooks below) can reach
  // `useDraftSession`'s setter without ordering issues.
  draftSessionRefForHooks.current = setDraftSession;

  // W2.5 OSNAP-OVERRIDE-MODES-S2 — local Codex review (round-2) P2: an
  // armed-but-unconsumed Extended (Apparent) Intersection override
  // (`osnapExtendedOverride`) must not survive past the draft session that
  // armed it. The FIRST fix attempt only cleared it in the GLOBAL window
  // Escape handler (`useKeyboardShortcuts.ts`), but this app has SEVERAL
  // other seams that end/replace a draft session WITHOUT going through that
  // handler — the command-input's OWN Escape branch (`preventDefault`s
  // before the window listener runs), the inline Esc BUTTON (a mouse click,
  // never reaches any keyboard handler), `resetShellState` (NEW drawing),
  // and the in-canvas text-editor's cancel — each of which would need its
  // own manual clear, exactly the "N call sites, forget one = silent bug"
  // anti-pattern `draftStepRegistry.ts`'s own header warns about. A single
  // BY-CONSTRUCTION effect reacting to the live `draftSession` value covers
  // every current AND future seam instead: clear the override whenever
  // there is no active draft session, OR the current step no longer accepts
  // a point pick (the override only ever makes sense while a point is being
  // requested — `getDraftStepDescriptor(...).acceptsPointInput` is the same
  // registry field `useStageContextMenu`'s menu-gate uses). Arming the
  // override does NOT itself change `draftSession`, so this effect never
  // fires (and so never clobbers) a fresh arm made between two point picks
  // of the SAME still-active step.
  //
  // local Codex round-9 P2 — the `acceptsPoint` check alone misses a
  // REPLACED session whose new step ALSO accepts a point (e.g. typing
  // CIRCLE while LINE is still waiting for its first point — the command
  // dispatcher allows switching commands mid-prompt). `draftSession` goes
  // straight from LINE's point-step to CIRCLE's point-step with no
  // intervening null/non-accepting state, so `acceptsPoint` stays true and
  // an override armed for LINE would leak into CIRCLE's first click. Track
  // the previous session's `command` id and ALSO clear whenever it changed
  // — a same-command step-to-step advance (LINE point 1 → point 2) keeps
  // `command` identical and so still preserves the override, matching the
  // documented same-session behaviour above; only an actual session
  // REPLACEMENT (different command, or ending into then out of `null`)
  // triggers the extra clear.
  // local Codex round-10 P2 — `commandChanged || !acceptsPoint` alone
  // misses a point committed through TYPED command-line / Dynamic-Input
  // text instead of a canvas click. A click-driven commit clears the
  // override itself as part of the SAME one-shot consume (`commitClick`'s
  // `setOsnapExtendedOverride(null)` right after `onCanvasPoint`), so this
  // effect never even needs to catch it there. A TYPED commit skips the
  // pick loop entirely — nothing ever consumes the override — yet it
  // advances the SAME command to its NEXT point-accepting step (LINE
  // first-point → next-point keeps `command` identical, and the next step
  // still `acceptsPointInput`), so neither existing trigger fires either.
  // The override, armed/captured for the OLD point, would then silently
  // swallow the NEXT canvas click as a stale operand pick instead of
  // letting it act as the new point. Track the previous session's
  // COMMITTED POINT COUNT (`draftSession.points.length`) alongside
  // `command` and ALSO clear whenever it changed — covers both the typed-
  // commit gap here and remains a harmless no-op on the click path (the
  // override is already null by the time this effect's `!= null` guard
  // runs there).
  const prevDraftCommandRef = useRef<DraftCommandId | null>(null);
  const prevDraftPointCountRef = useRef<number>(0);
  useEffect(() => {
    const currentCommand = draftSession?.command ?? null;
    const currentPointCount = draftSession?.points.length ?? 0;
    const commandChanged = currentCommand !== prevDraftCommandRef.current;
    const pointCountChanged =
      currentPointCount !== prevDraftPointCountRef.current;
    prevDraftCommandRef.current = currentCommand;
    prevDraftPointCountRef.current = currentPointCount;
    const acceptsPoint =
      draftSession != null &&
      getDraftStepDescriptor(draftSession.step).acceptsPointInput;
    if (
      (commandChanged || pointCountChanged || !acceptsPoint) &&
      useSysvarStore.getState().osnapExtendedOverride != null
    ) {
      useSysvarStore.getState().setOsnapExtendedOverride(null);
    }
  }, [draftSession]);

  // cmd-pan — enter the interactive realtime-pan session (bare PAN / right-click
  // 'Pan'). Flips the event-time ref + the render state together and surfaces the
  // prompt strip. The pre-pan ZOOM-Previous snapshot is NOT taken here (P2-2): it
  // is pushed lazily at the first real main-camera move of an LMB pan drag, so a
  // bare PAN exited without dragging (or a VP-redirected drag) leaves the ring
  // clean. Returns true when the session was actually entered, false when it could not
  // start (no wired stage) — the bare-PAN router arm honours this so it does NOT
  // claim success (`return true`) on a missing stage, mirroring the displacement
  // arm's `!api → return false` guard (cmd-pan FINDING 4a). An already-active
  // session returns true (the session IS active — no-op re-entry is success).
  const startPanSession = useCallback((): boolean => {
    if (panSessionActiveRef.current) return true;
    const api = stageCameraApiRef.current;
    if (!api) {
      appendCommandFeed(t("zoom.stage_not_ready"));
      return false;
    }
    // cmd-pan P2-5 — PAN supersedes any live ZOOM Window / ZOOM Object pick.
    // The stage gives a PAN session's LMB priority over `zoomPickingRef`, so an
    // un-cancelled zoom pick stays armed UNDERNEATH the pan and resumes
    // consuming clicks (its prompt + ref persist) after PAN ends. Cancel it
    // first — `cancelZoomPicking()` runs the pick's onCancel and clears both the
    // picking ref and the `zoomPickingPrompt` (wired via onZoomPromptChange) —
    // mirroring how every other mode-entry supersedes a live pick.
    api.cancelZoomPicking();
    // cmd-pan P2-2 — do NOT snapshot the view at session START. The pre-change
    // view is pushed onto the ZOOM-Previous ring lazily, at the FIRST real
    // main-camera move of an LMB pan drag (stage's `onPanSnapshotView`). A bare
    // PAN the user exits WITHOUT dragging, or a drag that starts over a redirected
    // VP (which moves the VP's model view, not the paper camera), thus pushes
    // NOTHING — instead of an identical entry ZOOM Previous would pop as a no-op.
    panSessionActiveRef.current = true;
    setPanSessionActive(true);
    appendCommandFeed(t("pan.realtime_prompt"));
    return true;
  }, [appendCommandFeed, t]);

  // cmd-pan — exit the interactive realtime-pan session (Esc/Enter on the canvas
  // or a programmatic stop). Clears the ref + state, drops the prompt, and feeds
  // the end line. The pre-pan view is already on the ZOOM-Previous ring (pushed
  // on the LMB drag via the adapter's `snapshotForHistory`), so ZP undoes it.
  //
  // cmd-pan P3-6 — `options.silent` skips the `pan.session_end` feed line. RESET
  // (`resetShellState`) clears the command feed and THEN tears the session down;
  // a non-silent teardown would re-append `PAN: panning ended.` onto the
  // freshly-cleared feed, leaving a stale message in the brand-new drawing. The
  // interactive Esc/Enter exit stays non-silent (it keeps its feed line); only
  // reset passes `{ silent: true }`.
  const endPanSession = useCallback(
    (options?: { silent?: boolean }) => {
      if (!panSessionActiveRef.current) return;
      panSessionActiveRef.current = false;
      setPanSessionActive(false);
      if (!options?.silent) appendCommandFeed(t("pan.session_end"));
    },
    [appendCommandFeed, t],
  );

  // cmd-pan P2-4 — the SINGLE "a direct (non-command-line) action is starting,
  // so supersede any active realtime-PAN first" choke point. `submitCommandText`
  // and `beginDraftingWithColorReset` already tear the session down at their
  // entry; but the File/quickbar ribbon callbacks (Import / Print / PageSetup /
  // Block-palette / style-editor dialogs) and any other DIRECT entry point bypass
  // BOTH — so without this the prompt strip + hand cursor persist and the next
  // canvas LMB is consumed as a pan. `supersedePanSession` is a no-op when no
  // session is active; `withPanSupersede` wraps a 0-arg callback so the whole
  // ribbon-callback bundle can funnel through ONE place (enumeration-complete:
  // every bypass entry point is wrapped, not patched one-by-one per @codex round).
  const supersedePanSession = useCallback(() => {
    if (panSessionActiveRef.current) endPanSession();
  }, [endPanSession]);

  const withPanSupersede = useCallback(
    <Args extends unknown[]>(fn: (...args: Args) => void) =>
      (...args: Args): void => {
        supersedePanSession();
        fn(...args);
      },
    [supersedePanSession],
  );

  // cmd-pan r5 P2-1 — BY-CONSTRUCTION reactive PAN teardown (the
  // convergence-breaker). The `supersedePanSession()` / `withPanSupersede` choke
  // point above is a fine FAST-PATH, but it only fires on the entry points that
  // happen to call it — and direct dispatchers bypass it (the space-routing card
  // dispatches `shell/activate-space` / `shell/set-layout-context` WITHOUT going
  // through the `activateSpace` wrapper; drag-and-drop import calls
  // `importDxfFileWithHydration` directly). Round after round we wrapped one more
  // entry point and the next round found another. The two effects below end the
  // spiral: they react to the RESULTING reactive state, so ANY trigger — card,
  // tab, command line, programmatic dispatch, DnD, NEW/reset — is covered without
  // wrapping its call site.
  //
  // `endPanSession`'s identity is UNSTABLE — its `useCallback` deps include
  // `appendCommandFeed`, a plain function re-created on every render by
  // `useCommandFeed`. If the effects depended on `endPanSession`, they would
  // re-run on EVERY render (e.g. the feed write inside `startPanSession` itself)
  // and tear the session down the instant it started. So the teardown is read
  // through a LATEST-ref (mirrors `submitCommandTextRef`) and the effects depend
  // ONLY on the reactive TRIGGER values — they fire iff the space/layout/document
  // actually changed, never on incidental re-renders.
  const endPanSessionRef = useRef(endPanSession);
  endPanSessionRef.current = endPanSession;

  // Effect A — the active space OR the layout context changed. `editorActiveSpaceId`
  // (`state.drawing.activeSpaceId`) changes on `shell/activate-space` from ANY
  // source; `editorLayoutContext` (`state.drawing.layoutContext`) changes on
  // `shell/set-layout-context` (the paper↔viewport toggle, which keeps the same
  // space id). Depending on BOTH catches every space/layout transition. The pan
  // belonged to the OLD space's camera, so it must not persist into the new one.
  // Silent teardown: the transition is a navigation, not a deliberate PAN exit —
  // no `pan.session_end` feed line (mirrors the reset path's silent end).
  useEffect(() => {
    if (panSessionActiveRef.current) endPanSessionRef.current({ silent: true });
  }, [editorActiveSpaceId, editorLayoutContext]);

  // Effect B — the document was REPLACED (DXF import via picker OR drag-and-drop,
  // or NEW/reset). `geometryDocumentId` (`geometryDocument.id`) is minted fresh
  // on every document replacement, so this single dependency catches the DnD
  // import path (`handleWorkspaceDrop → importDxfFileWithHydration`) that bypasses
  // the choke point, plus picker-import and reset (which already supersede via
  // their own seams — this is the redundant by-construction backstop). A pan
  // session can never survive into a freshly-loaded drawing whose camera + spaces
  // are brand new. Silent — the feed is cleared / replaced by the import status.
  useEffect(() => {
    if (panSessionActiveRef.current) endPanSessionRef.current({ silent: true });
  }, [geometryDocumentId]);

  // CECOLOR round-8 FINDING D — starting ANY non-colour draft command supersedes
  // a still-armed `-COLOR` keyword prompt. `-COLOR` arms `colorPromptPending`,
  // which the pending-reply consumer (top of `submitCommandText`) clears only on
  // a command-line reply or reset; that consumer runs only when `!draftSession`.
  // So if the user starts a draft command (ribbon button, toolbar, or typed
  // command) while the prompt is pending, the draft runs with the flag still
  // set, and the NEXT command-line submission after the draft would be wrongly
  // consumed as a colour reply. Route EVERY draft-command start through this
  // wrapper (ribbon passes `beginDrafting` DIRECTLY, bypassing `submitCommandText`
  // — so clearing only there would miss the ribbon-click path) to clear the
  // pending flag the moment a real draft begins. Colour is never a draft command,
  // so no draft start legitimately needs the flag — clearing here is always safe.
  // CECOLOR round-9 ROOT CURE — the SINGLE shared "end the colour-input context"
  // clearer. Three pieces of transient colour-input state must be cleared
  // together at EVERY seam that ends or replaces the colour-input context:
  //   1. `ceColorPickerOpen` — the command-line COLOR "Select Color" picker.
  //   2. `colorPromptPending` — the dialog-less `-COLOR` keyword-reply consumer.
  //   3. the swatch's LOCAL picker open-state — out of React reach (component
  //      useState in CeColorSwatch); closed by bumping the store `resetEpoch`
  //      the swatch subscribes to.
  // Each prior round patched ONE seam in isolation (Escape, draft-start, reset,
  // …), which let every NEW seam (swatch commit, file-open replace) re-surface
  // the same stale-picker / swallowed-command class of bug. Routing EVERY
  // context-ending seam through this one clearer is the seam-completeness fix
  // that ends the per-round churn. NOTE: this is the GENERIC context-end clearer
  // — `resetShellState` additionally resets `doc.cecolor` → ByLayer + bumps the
  // stage token (reset-only, NOT folded in here); the swatch-COMMIT seam uses the
  // narrower `colorCommitEpoch` signal (it must NOT re-close its own picker
  // mid-commit), so it does NOT call this.
  const clearColorTransientState = useCallback(() => {
    setCeColorPickerOpen(false);
    setColorPromptPending(false);
    // The swatch's local picker open-state is out of React reach — bump the
    // store epoch it subscribes to so it closes its own picker too.
    useCeColorStore.getState().bumpResetEpoch();
  }, []);

  const beginDraftingWithColorReset = useCallback(
    (command: DraftCommandId, cmdOptions?: Record<string, string>) => {
      // round-9 — supersede the colour-input context (close pickers + clear the
      // pending `-COLOR` reply) the moment a real draft begins (round-8 D
      // generalised to the shared clearer).
      clearColorTransientState();
      // cmd-pan FINDING 1 — a draft command supersedes an active realtime-pan
      // session at the SAME draft-start choke point colour uses (ribbon passes
      // `beginDrafting` through here too). Without this the prompt strip + hand
      // cursor persist and the LINE's first canvas LMB-down is consumed as a pan
      // (stageInteractions checks `panSessionActiveRef` before the draft click).
      if (panSessionActiveRef.current) endPanSession();
      beginDrafting(command, cmdOptions);
    },
    [beginDrafting, clearColorTransientState, endPanSession],
  );

  // Track 3 Phase 1 — BlockEditExitDialog feature. Owns its own
  // open/close flag, auto-open effect, and Save/Discard/Cancel
  // handlers so App.tsx stays free of BCLOSE-specific logic.
  const blockEditExitDialog = useBlockEditExitDialog({
    draftSession,
    setDraftSession,
    setCommandText,
    appendCommandFeed,
  });

  const activeSpace = selectActiveSpace(state);
  const activeEntities = selectEntitiesInActiveSpace(state);
  const modelSpaceEntities = selectModelSpaceEntities(state);
  const selectedEntities = selectSelectedEntities(state);

  // Track 3 Phase 2 — BLOCK / INSERT dialog hooks. Each owns its own
  // open flag, auto-open effect (driven by `draftSession.step`), and
  // Confirm / Cancel handlers. The orchestrator threads them with the
  // overlap interceptor from `useOverlapWarningDialog`.
  const blockDefinitionDialog = useBlockDefinitionDialog({
    draftSession,
    selectedEntities,
    setDraftSession,
    setCommandText,
    appendCommandFeed,
  });
  const insertBlockDialog = useInsertBlockDialog({
    draftSession,
    setDraftSession,
    setCommandText,
    appendCommandFeed,
    interceptOverlapForBlockInsert:
      overlapWarningDialog.interceptOverlapForBlockInsert,
  });

  // Track 3 Phase 3 — Style editor dialog hooks. FontManager is mounted
  // first so that `useTextStyleEditorDialog` can wire its "Manager..."
  // button straight to the FontManager hook's `open()`.
  const fontManagerDialog = useFontManagerDialog();
  const dimStyleEditorDialog = useDimStyleEditorDialog({ propertyDraft });
  const mleaderStyleEditorDialog = useMLeaderStyleEditorDialog({
    propertyDraft,
  });
  const textStyleEditorDialog = useTextStyleEditorDialog({
    propertyDraft,
    onOpenFontManager: fontManagerDialog.open,
  });

  // CC-3 SpecialEditorHost controller (REVCLOUD-PROPS-EDITOR §5.1). Owns which
  // entity's special-editor dialog is open + re-resolves it from the live
  // document; `REVCLOUDPROPERTIES` / a future ribbon action calls `.open(id)`.
  const specialEditor = useSpecialEditorHost();

  // Track 3 Phase 4 — XREF Manager / PRINT / SCALELISTEDIT / Annotative
  // Object Scale dialog hooks. Each owns its own open flag and handlers;
  // the command-line router and Properties palette call their `open()`
  // methods, and `resetShellState()` calls `close()` to clean up.
  const xrefDialog = useXrefDialog({ appendCommandFeed });
  // PDF-6.9b — PDF Options sub-dialog. Declared BEFORE `printDialog` so the
  // PRINT path can capture `pdfOptionsDialog.getConfig` as its non-reactive
  // PDF-Options getter. Launched from the Page Setup Manager footer;
  // `getConfig()` is also read at PUBLISH time by `SpaceTabsBar`.
  // `resolveLayers` snapshots `styleTables.layers` each open() so a layer
  // added/removed since the last open is reflected.
  const pdfOptionsDialog = usePdfOptionsDialog({
    appendCommandFeed,
    resolveLayers: () => {
      const doc = useEditorStore.getState().geometryDocument;
      const layers = doc.styleTables?.layers ?? [];
      return layers.map((l) => ({ name: l.name }));
    },
  });
  const printDialog = usePrintDialog({
    appendCommandFeed,
    // PDF-6.9b — thread the committed PDF Options config (vector DPI,
    // omit-layers) into the PRINT exporter at confirm-time. `getConfig` is
    // already non-reactive (a ref read inside `usePdfOptionsDialog`), so
    // the print preview never re-renders when the user edits PDF Options.
    getPdfOptionsConfig: pdfOptionsDialog.getConfig,
  });
  // PDF-6.9a — PAGESETUP / ПАРАМЛИСТ owns the per-layout page setup
  // manager. Mirrors `usePrintDialog`'s shape: open()/close() + props
  // bundle for the dialog. Each layout has its own draft; Apply writes
  // every dirty draft via `setLayoutPlotSettings`.
  const pageSetupDialog = usePageSetupDialog({ appendCommandFeed });
  const scaleListEditDialog = useScaleListEditDialog();
  const annotativeObjectScaleDialog = useAnnotativeObjectScaleDialog();
  // Track 3 Phase 5 — BPM / BATTMAN / EATTEXT / RefEdit hooks. Each
  // owns the matching dialog open flag, props bundle, and command-feed
  // wiring; `resetShellState()` drives `close()` on the dialog hooks.
  const bpmDialog = useBpmDialog({ appendCommandFeed });
  const battmanDialog = useBattmanDialog({ appendCommandFeed });
  const eattextDialog = useEattextDialog({ appendCommandFeed });
  const refEditWorkflow = useRefEditWorkflow();

  // Track 3 Phase 6 — ATTDEF / DDATTE / EATTEDIT dialog handlers + the
  // BlockAuthoring palette/shell state. Both hooks bundle previously
  // free-floating App.tsx state into a tight imperative surface.
  const attributeDialogs = useAttributeDialogs({
    draftSession,
    setDraftSession,
    setCommandText,
    appendCommandFeed,
  });
  const blockAuthoringShell = useBlockAuthoringShell();

  // PR-V4 — subscribe to the index version counter so panel selectors
  // re-run after every metadata-index rebuild even though the index
  // itself lives outside Zustand reactive state.
  const entityMetadataIndexVersion = useEditorStore(
    (store) => store.entityMetadataIndexVersion,
  );
  // PR-V4 — entity-list panel virtualization: read directly from the
  // metadata index (covers cache-stash huge imports) and cap visible
  // rows at ENTITY_LIST_VIEW_CAP. The remaining N - cap rows are
  // surfaced via the "show all" affordance below the cap message.
  const activeSpaceMetadata = useMemo(
    () => getEntityMetadataIndex().filterBySpace(activeSpace.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- index is non-reactive; version bump is the trigger
    [activeSpace.id, entityMetadataIndexVersion],
  );
  // PR-V4 — per-layer entity count map for LayerManagerDialog. Pulled
  // off the aggregates snapshot so the dialog sees correct counts on
  // huge-import flows where state.drawing.entities is empty.
  const entityCountsByLayer = useMemo(
    () => getEntityMetadataIndex().aggregates().byLayer,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- non-reactive index; bump triggers re-evaluation
    [entityMetadataIndexVersion],
  );
  // PR-LT3 variant A (A2) — FIX-1 (Codex P1). Stabilise the `linetypeSysvars`
  // object identity. A fresh object literal in the `<Scene3DStage>` props would
  // change reference on EVERY App render, so `useSceneEffects` (which lists it
  // in a dep array) would tear down + rebuild ALL 3D buckets via
  // `runSceneBulkLoad` on every selection / unrelated UI update — catastrophic
  // on large imports. Memoising on the three numeric sysvars keeps the
  // reference stable unless a value actually changes.
  const linetypeSysvars = useMemo(
    () => ({
      LTSCALE: geometryDocument.ltscale ?? 1,
      MSLTSCALE: geometryDocument.msltscale ?? 1,
      PSLTSCALE: geometryDocument.psltscale ?? 1,
    }),
    [
      geometryDocument.ltscale,
      geometryDocument.msltscale,
      geometryDocument.psltscale,
    ],
  );
  // PR-V3 — when activeEntities is empty (huge-import path) the
  // metadata index is the source of truth for "how many objects in
  // the active space?" used by the entity-list panel header. Falls
  // back to activeEntities.length for small files where the reactive
  // list is populated.
  const activeEntitiesCount =
    activeEntities.length > 0
      ? activeEntities.length
      : activeSpaceMetadata.length;
  // ── B11 — print-preview world rectangle ──
  // Owned by `usePrintDialog` (Track 3 Phase 4). The hook returns
  // `printPreviewWorldBounds` which is `null` when the dialog is closed
  // and otherwise the aspect-fitted paper rectangle around the
  // drawing's centroid (re-derives on settings change + index version).
  const propertiesView = derivePropertiesView(state);
  const matchpropSourceEntity = selectMatchpropSourceEntity(state);
  const textStyles = geometryDocument.styleTables?.textStyles ?? [];
  const dimensionStyles = geometryDocument.styleTables?.dimensionStyles ?? [];
  const multileaderStyles =
    geometryDocument.styleTables?.multileaderStyles ?? [];
  const isLayoutSpace = activeSpace.kind === "layout";
  // Reactive subscription to space context mode — needed to gate the
  // ViewportInspector to PSPACE only (exclude MSPACE_VIA_VP sessions).
  const spaceContextMode = useSpaceContextStore((s) => s.mode);

  function getStyleOptions(label: string): string[] {
    switch (label) {
      case "Text style":
        return textStyles.map((s) => s.name);
      case "Dimension style":
        return dimensionStyles.map((s) => s.name);
      case "Multileader style":
        return multileaderStyles.map((s) => s.name);
      default:
        return [];
    }
  }

  // ── Phase MC5 — ViewportInspector: compute selected VP + all layer names ──
  // Show the ViewportInspector panel when:
  //   1. Exactly one entity is selected
  //   2. That entity is a Viewport type
  //   3. The active space is a layout (PSPACE) AND mode is not MSPACE_VIA_VP.
  //      MSPACE_VIA_VP sessions run inside a layout but the user is editing
  //      model content — not managing viewports — so the inspector must hide.
  const selectedViewportEntity = (() => {
    if (selectedEntities.length !== 1) return null;
    if (selectedEntities[0].type !== "Viewport") return null;
    if (!isLayoutSpace || spaceContextMode === "MSPACE_VIA_VP") return null;
    const vpId = selectedEntities[0].id;
    for (const space of geometryDocument.spaces) {
      const found = space.entities.find((e) => e.id === vpId && e.type === "Viewport");
      if (found) return found as import("@dxf-ai-editor/drawing-model").ViewportEntity;
    }
    return null;
  })();

  const allLayerNamesForVP = (() => {
    if (!selectedViewportEntity) return [];
    return (geometryDocument.styleTables?.layers ?? []).map((l) => l.name);
  })();

  const contextEntityType: string | null = (() => {
    if (selectedEntities.length === 0) return null;
    const firstType = selectedEntities[0].type;
    return selectedEntities.every((e) => e.type === firstType)
      ? firstType
      : null;
  })();

  // DYN-1.10 — classify a property-grid row as a dynamic block property
  // (Param: <name> or Visibility). Returns the descriptor the
  // PropertyGrid uses to render the right control (numeric / toggle /
  // dropdown). Returns null for static properties so the default input
  // path is used.
  function getDynamicPropertyDescriptor(
    rowLabel: string,
  ): import("./components/PropertyGrid").DynamicPropertyDescriptor | null {
    if (selectedEntities.length !== 1) return null;
    const entity = selectedEntities[0];
    if (!entity || entity.type !== "Block") return null;
    const blockName = entity.properties["Name"];
    if (!blockName) return null;
    const blockDef = geometryDocument.blocks?.[blockName];
    if (!blockDef?.dynamicMetadata) return null;
    if (rowLabel === "Visibility") {
      const vis = blockDef.dynamicMetadata.parameters.find(
        (p) => p.kind === "visibility",
      );
      if (vis && vis.kind === "visibility") {
        return {
          kind: "visibility",
          states: vis.states.map((s) => ({ id: s.id, name: s.name })),
        };
      }
      return null;
    }
    if (rowLabel.startsWith("Param: ")) {
      const after = rowLabel.slice("Param: ".length);
      // DYN-2 Tier 2 parameters expose multiple property-grid rows per
      // parameter using ".x" / ".y" / ".distance" / ".angle" suffixes.
      // Parse the suffix here so the descriptor picks the correct
      // kind-specific value set for that axis.
      let paramName = after;
      let suffix: "x" | "y" | "distance" | "angle" | null = null;
      const m = after.match(/^(.*)\.(x|y|distance|angle)$/);
      if (m) {
        paramName = m[1];
        suffix = m[2] as "x" | "y" | "distance" | "angle";
      }
      const param = blockDef.dynamicMetadata.parameters.find(
        (p) => p.name === paramName,
      );
      if (!param) return null;
      if (param.kind === "linear") {
        const listValues = param.valueSet?.listValues;
        const descriptor: import("./components/PropertyGrid").DynamicPropertyDescriptor =
          {
            kind: "linear",
            valueSet: {
              mode: param.valueSet?.mode ?? "none",
              ...(listValues ? { listValues } : {}),
            },
          };
        return descriptor;
      }
      if (param.kind === "flip") return { kind: "flip" };
      if (param.kind === "basepoint") return { kind: "basepoint" };
      if (param.kind === "point") {
        const vs = suffix === "y" ? param.yValueSet : param.xValueSet;
        const listValues = vs?.listValues;
        return {
          kind: suffix === "y" ? "point-y" : "point-x",
          valueSet: {
            mode: vs?.mode ?? "none",
            ...(listValues ? { listValues } : {}),
          },
        };
      }
      if (param.kind === "polar") {
        const isAngle = suffix === "angle";
        const vs = isAngle ? param.angleValueSet : param.distanceValueSet;
        const listValues = vs?.listValues;
        return {
          kind: isAngle ? "polar-angle" : "polar-distance",
          valueSet: {
            mode: vs?.mode ?? "none",
            ...(listValues ? { listValues } : {}),
          },
        };
      }
      if (param.kind === "xy") {
        const vs = suffix === "y" ? param.yValueSet : param.xValueSet;
        const listValues = vs?.listValues;
        return {
          kind: suffix === "y" ? "xy-y" : "xy-x",
          valueSet: {
            mode: vs?.mode ?? "none",
            ...(listValues ? { listValues } : {}),
          },
        };
      }
      if (param.kind === "rotation") {
        const listValues = param.angleValueSet?.listValues;
        return {
          kind: "rotation",
          valueSet: {
            mode: param.angleValueSet?.mode ?? "none",
            ...(listValues ? { listValues } : {}),
          },
        };
      }
      if (param.kind === "lookup") {
        const tables = blockDef.dynamicMetadata.lookupTables ?? [];
        const table = tables.find((t) => t.id === param.lookupTableId);
        const rows = (table?.rows ?? []).map((r) => ({
          id: r.id,
          label: param.reverseLookup
            ? r.outputValue !== undefined
              ? String(r.outputValue)
              : r.id
            : r.id,
        }));
        return {
          kind: "lookup",
          rows,
          reverseLookup: param.reverseLookup,
        };
      }
      // DYN-2 Batch C — Tier 2 dimensional constraint parameters expose
      // a single numeric field; the property-grid uses the same numeric
      // input rendering as Linear / Rotation but routes the value back
      // via the constraint-* descriptor kind.
      if (
        param.kind === "constraint-horizontal" ||
        param.kind === "constraint-vertical"
      ) {
        const listValues = param.distanceValueSet?.listValues;
        return {
          kind: param.kind,
          valueSet: {
            mode: param.distanceValueSet?.mode ?? "none",
            ...(listValues ? { listValues } : {}),
          },
        };
      }
      if (param.kind === "constraint-angular") {
        const listValues = param.angleValueSet?.listValues;
        return {
          kind: "constraint-angular",
          valueSet: {
            mode: param.angleValueSet?.mode ?? "none",
            ...(listValues ? { listValues } : {}),
          },
        };
      }
      // DYN-3 Batch E — Tier 3 descriptors.
      // - Alignment exposes a current angle (numeric, degrees) via
      //   <name>.angle (same suffix convention as Polar).
      // - Aligned / Radial / Diameter constraints expose a numeric
      //   distance / radius / diameter via <name> (bare).
      if (param.kind === "alignment") {
        return {
          kind: "alignment",
          valueSet: { mode: "none" },
        };
      }
      if (param.kind === "constraint-aligned") {
        const listValues = param.distanceValueSet?.listValues;
        return {
          kind: "constraint-aligned",
          valueSet: {
            mode: param.distanceValueSet?.mode ?? "none",
            ...(listValues ? { listValues } : {}),
          },
        };
      }
      if (param.kind === "constraint-radial") {
        const listValues = param.radiusValueSet?.listValues;
        return {
          kind: "constraint-radial",
          valueSet: {
            mode: param.radiusValueSet?.mode ?? "none",
            ...(listValues ? { listValues } : {}),
          },
        };
      }
      if (param.kind === "constraint-diameter") {
        const listValues = param.diameterValueSet?.listValues;
        return {
          kind: "constraint-diameter",
          valueSet: {
            mode: param.diameterValueSet?.mode ?? "none",
            ...(listValues ? { listValues } : {}),
          },
        };
      }
      return null;
    }
    return null;
  }

  // Pass current sysvar values so $DYNMODE / $DYNDIGRIP / … are persisted
  // in the HEADER section of the exported DXF (AutoCAD parity — Phase 4).
  //
  // Phase 1.j (П35Р local-iteration) — memoize the export. The full
  // DXF serializer iterates every entity in `geometryDocument.spaces`;
  // on a 685k-entity drawing this takes ~30-60s of sync main-thread
  // work. Running it on EVERY render (which fired hundreds of times
  // post-import during the React reconciliation storm) was the
  // dominant cost AFTER the import settled, and contributed to
  // React error #185 by stacking renders beyond the depth budget.
  // Memoize on `geometryDocument` identity so the export only fires
  // when the document actually changes.
  // Codex P2 follow-up — subscribe to sysvar values so the export
  // re-runs when $DYNMODE / DIMSCALE / ... change, not just when the
  // geometry document changes. Reading via the hook (instead of
  // `getState()`) makes the values object a real dep — Zustand still
  // bails out on Object.is so unrelated sysvar slices don't trigger a
  // re-export. The values object is replaced on every `setVar`, which
  // is exactly when the export needs to be refreshed.
  const sysvarValues = useSysvarStore((s) => s.values);
  // CECOLOR — live current colour (sibling store; a union cannot ride the
  // integer-only sysvar schema). Subscribed as a hook value so the export memo
  // recomputes when the user changes the current colour (mirrors sysvarValues).
  const liveCeColor = useCeColor((s) => s.ceColor);
  // CECOLOR round-9 FINDING A — subscribe to the swatch "colour committed" epoch
  // so a colour picked through the ribbon swatch (a child component out of reach
  // of this App-level setter) clears the armed dialog-less `-COLOR` pending-reply
  // flag. Without this, picking via the swatch while `-COLOR` is armed leaves
  // `colorPromptPending` set and the next command-line submission (e.g. LINE) is
  // wrongly swallowed by the colour-reply consumer. The swatch bumps this epoch
  // on commit (`bumpColorCommitEpoch`); the effect below clears the pending flag
  // on change. The swatch already subscribes to this store, so reading one more
  // slice adds NO new module edge (mirrors the round-8 resetEpoch pattern).
  const colorCommitEpoch = useCeColor((s) => s.colorCommitEpoch);
  // CECOLOR round-9 FINDING A — clear the armed `-COLOR` pending-reply flag when
  // the ribbon swatch commits a colour (the prompt is satisfied/moot once the
  // swatch applied a colour). The initial-mount run is a harmless no-op
  // (`colorPromptPending` is already false at mount — mirrors the swatch's own
  // resetEpoch effect). We clear ONLY the pending flag here (not the full
  // transient clearer): the command-line picker is unrelated to a swatch commit,
  // and re-bumping `resetEpoch` would close the swatch's own just-used picker.
  useEffect(() => {
    setColorPromptPending(false);
  }, [colorCommitEpoch]);
  // Codex round 3 #6 (PR #87) — convert the export from an eager memo
  // returning a string to a stable callback returning a builder
  // function. The previous memo recomputed on every
  // `entityMetadataIndexVersion` bump (every commitMutation), and
  // `geometryDocument` identity also changes on every mutation, so the
  // memo serialised the entire 685k-entity DXF document on every
  // single edit — blocking the main thread for ~30-60s per mutation.
  //
  // The fix: `buildExportedDxfText` is a `useCallback` whose deps
  // mirror the inputs of the serialisation, but the heavy work runs
  // ONLY when a consumer invokes it (Ctrl+S download, download button,
  // copy button, preview-effect below). The memoised callback itself
  // is allocation-only — no DXF text is built until called. The
  // `entityMetadataIndexVersion` dep is preserved so that the
  // callback identity changes after a streaming-import chunk callback
  // mutates the compact cache (the preview-effect below uses the
  // identity change as a re-trigger signal for SMALL drawings only).
  const buildExportedDxfText = useCallback((
    // PR1b Codex P2 (iter-5) — `emitAuditWarning` (default false) splits the
    // preview path from the user-export path. `auditExportReferences()` always
    // returns the CORRECT per-space snapshot (streaming provenance +
    // linetypeScale) so the preview serialises the right entities; but it only
    // EMITS the warn-only "unresolved references" transcript warning when a USER
    // initiates the export (Download / Copy / Ctrl+S). The auto-preview /
    // render-driven re-serialise effect below calls WITHOUT the flag, so an
    // ordinary import/edit no longer pushes a "save" warning the user never
    // triggered. Keeps the snapshot single-source (one build per call) and the
    // iter-4 dedupe + document.id key behaviour for the user-export path.
    options?: { emitAuditWarning?: boolean },
  ): string => {
    // Codex round 2 #8 (PR #87) — streaming OPEN path materialises
    // entities into the compact cache and commits a `geometryDocument`
    // whose inline `space.entities[]` arrays are empty. Feeding that
    // directly to `exportDrawingDocumentToDxfString` would emit an
    // empty DXF (ENTITIES section with zero records). The export
    // function consumes `entitiesSnapshot[spaceId]` in preference to
    // `space.entities` (Phase 4 Wave 1 Task C contract — see
    // `dxfBridge.export.ts` `ExportDrawingDocumentToDxfStringOptions`).
    //
    // PR1b Codex P2 (iter-3) — route the snapshot build + warn-only audit
    // through the SHARED store action `auditExportReferences()` so the
    // download / copy / preview paths run the EXACT same per-space snapshot
    // logic AND the same single audit-emit point as the `exportDxfText` store
    // action. Previously this local builder duplicated the (now-fixed)
    // all-spaces-empty gate and bypassed the audit entirely, so a
    // user-triggered DXF download could silently write an unresolved ref the
    // DevTools helper warned about. The store action emits the warning (once,
    // deduped) and returns the per-space snapshot for the serialise below.
    const entitiesSnapshot = useEditorStore
      .getState()
      .auditExportReferences({
        emitWarning: options?.emitAuditWarning === true,
      });
    // Codex #342 F1 — project the LIVE sysvar store into the exported
    // document's typed HEADER fields (MIRRTEXT / OSMODE) so a SETVAR-then-save
    // persists the live value, not the stale document field. Shared root helper
    // with the store `exportDxfText` action (no per-site clone). See
    // `applyLiveHeaderSysvars` for the rationale + round-trip no-op guarantee.
    const exportDocument: DrawingDocument = applyLiveHeaderSysvars(
      geometryDocument,
      { MIRRTEXT: sysvarValues.MIRRTEXT, OSMODE: sysvarValues.OSMODE },
    );
    // CECOLOR — project the live current colour onto the export document (no-op
    // when live == doc.cecolor). Shared root helper with the store path.
    const exportDocumentWithCeColor: DrawingDocument = applyLiveCeColor(
      exportDocument,
      liveCeColor,
    );
    // DXF-CONFORMANT-SKELETON — this download / copy / preview seam is a genuine
    // user-facing SAVE, so it opts into conformant-skeleton back-fill: a File →
    // New drawing (no imported raw source) is exported with the full structural
    // sections (HEADER / CLASSES / VPORT / APPID / BLOCK_RECORD / BLOCKS /
    // OBJECTS root dict) + hex entity handles so it opens in AutoCAD. Imported
    // documents are unaffected (carry-forward returns them unchanged).
    return exportDrawingDocumentToDxfString(exportDocumentWithCeColor, {
      dynVars: sysvarValues,
      ensureConformant: true,
      ...(entitiesSnapshot !== undefined ? { entitiesSnapshot } : {}),
    });
    // `entityMetadataIndexVersion` is intentionally a dep even though
    // it is not referenced inside the callback body: it's a re-trigger
    // signal for the preview-effect after a streaming-import chunk
    // callback mutates the compact cache (the cache itself lives
    // outside the Zustand reactive state, so the version counter is
    // the only signal the cache content changed).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geometryDocument, sysvarValues, liveCeColor, entityMetadataIndexVersion]);
  // PR1b Codex P2 (iter-5) — Ctrl+S is a USER-initiated export (mirrors the
  // Download button), so it MUST emit the warn-only audit warning. The ref keeps
  // its `() => string` contract for `useKeyboardShortcuts`; the wrapper threads
  // `emitAuditWarning: true` so the keyboard path matches the button paths.
  const exportedDxfTextRef = useRef<() => string>(() =>
    buildExportedDxfText({ emitAuditWarning: true }),
  );
  exportedDxfTextRef.current = () =>
    buildExportedDxfText({ emitAuditWarning: true });

  // ABB-S7c — a PARALLEL stable ref for the AGENT export.dxf seam. The agent's
  // read of `window.__editDwgExportedDxfText` is NOT a user-initiated export, so
  // it MUST use the side-effect-FREE variant: `buildExportedDxfText()` with NO
  // args defaults `emitAuditWarning` falsy → `auditExportReferences({emitWarning:
  // false})` pushes NO transcript warning. Crucial because the bridge's own
  // observers report the audit stream back; a warning push here would
  // self-contaminate that stream (and could evict a real entry from the 24-slot
  // audit ring). The produced DXF bytes are identical to the user-export ref —
  // only the audit side-effect differs. Reassigned every render so it tracks the
  // latest `buildExportedDxfText` closure (fresh document / sysvar state).
  const agentExportRef = useRef<() => string>(() => buildExportedDxfText());
  agentExportRef.current = () => buildExportedDxfText();

  // Codex round 3 #6 (PR #87) — preview text for the read-only DXF
  // export textarea. Decoupled from the on-demand `buildExportedDxfText`
  // callback so that the preview only auto-refreshes for SMALL
  // drawings; large imports keep the prior preview until the user
  // explicitly clicks "Download" / "Copy" (which call the builder
  // on-demand) or until the entity count drops below the threshold.
  //
  // Without this gating the textarea would serialise the entire DXF
  // on every render that touches the preview path — including the
  // hundreds of post-import reconciliation renders — which is the bug
  // Codex round 3 flagged.
  //
  // Threshold rationale: 5000 entities serialise in <50ms on a typical
  // dev machine. Above that the per-mutation cost is noticeable; above
  // 100k it blocks the main thread for >1s.
  const DXF_PREVIEW_AUTO_REFRESH_LIMIT = 5000;
  const [dxfPreviewText, setDxfPreviewText] = useState<string>("");
  useEffect(() => {
    // Skip the preview build for large drawings — the textarea stays
    // showing the last preview (typically empty on first load) and
    // the user uses Download/Copy buttons for the actual export.
    //
    // Codex round-5 #11 + autocheck follow-up (2026-05-18) — two
    // gates instead of one, because metadata-index size is the
    // authoritative steady-state signal but lags `geometryDocument`
    // by a few yields on the async streaming-import path.
    //
    // 1. If an import is actively progressing, skip the preview
    //    build unconditionally. `importProgress.active` is set
    //    synchronously at import start and cleared at completion;
    //    while it's true the document is mid-commit and any
    //    serialization attempt would race with the metadata
    //    rebuild + chunked entity append. This handles the gap
    //    Codex flagged: async import commits a new
    //    `geometryDocument` BEFORE `rebuildEntityMetadataIndex
    //    FromImportAsync` finishes, so `getEntityMetadataIndex
    //    ().size` is still 0 (or the prior document's size) while
    //    the new — potentially huge — drawing is already wired
    //    into the doc.
    // 2. Once import settles (`importProgress.active === false`),
    //    the metadata index reflects every entity-bearing surface
    //    (both `importDxfText{,Async}` and command-engine commits
    //    route through `entityMetadataIndex.applyBatch`), so the
    //    O(1) size read is the right gate.
    //
    // The `geometryDocument.spaces[].entities.length` reduce that
    // round-5 #11 dropped is NOT re-introduced — on cache-backed
    // documents the wrapper getter triggers a 685k snapshot
    // materialization at the first read post-import, which was the
    // original heap-spike round-5 wanted to avoid.
    // Autocheck follow-up (2026-05-18) — guard with optional chaining
    // because `state.importProgress` can be `undefined` during the
    // brief window before the editor store hydrates the initial
    // import-progress shell. The previous unguarded
    // `state.importProgress.active` raised
    // `TypeError: Cannot read properties of undefined` and tripped
    // the root ErrorBoundary on first paint of every fresh session.
    if (state.importProgress?.active === true) {
      return;
    }
    // PART B (OOM fix) — timing-independent large-import guard. The
    // metadata-index size read below is authoritative ONLY because
    // `rebuildEntityMetadataIndexFromImportAsync` is awaited BEFORE the
    // streaming import flips `importProgress.active` false (editorStore
    // `importDxfFileAsync`). That ordering is an implicit invariant this
    // effect must not blindly trust: if a future refactor clears the
    // import progress before the index rebuild settles, the size read
    // would be a stale 0 on the `true→false` re-fire and this effect
    // would serialise the ENTIRE 25 MB+ streamed document (wasted build +
    // heap) before the DOM-value cap above clips it. A streamed import
    // always materialises its entities into the compact cache and marks
    // the spaces via `markSpaceStreamed`, so a non-empty streamed-space
    // set is a stable, O(1), rebuild-ordering-independent signal that the
    // active document is a large streamed drawing — skip the auto-build
    // unconditionally and let the user click Download/Copy (on-demand) for
    // the full export. This NEVER engages for small inline imports
    // (`peekStreamedSpaceIds` is empty unless the streaming OPEN path ran).
    if (peekStreamedSpaceIds(getActiveDocumentId()).size > 0) {
      return;
    }
    const totalCount = getEntityMetadataIndex().size;
    if (totalCount > DXF_PREVIEW_AUTO_REFRESH_LIMIT) {
      return;
    }
    // PR1b Codex P2 (iter-5) — preview build: NO `emitAuditWarning`, so the
    // store skips the warn-only audit transcript emit (preview is not a user
    // export). The snapshot source is still correct (audit builds it either way).
    setDxfPreviewText(buildExportedDxfText());
    // `buildExportedDxfText` identity tracks `geometryDocument`,
    // `sysvarValues`, `entityMetadataIndexVersion`. Adding
    // `state.importProgress.active` as a dep so the effect re-fires
    // when the import-active gate flips (autocheck follow-up
    // 2026-05-18): the false→true transition skips the preview
    // build; the true→false transition runs the build once the
    // metadata index has caught up with the committed document.
  }, [buildExportedDxfText, state.importProgress?.active]);
  // PART A (OOM fix — PRIMARY). Cap the string that reaches the
  // export-preview <textarea> DOM `value`. The 33.5M-px synchronous Blink
  // layout comes from the rendered VALUE length, so the cap MUST be on the
  // value prop, not just the auto-build gate above — even if a full 25 MB+
  // build slips through, the DOM never holds more than DXF_PREVIEW_DOM_CAP
  // chars, so the giant layout can never happen. The cap + marker live in
  // `dxfPreviewCap.ts` (pure, unit-tested, single-sourced); below CAP the
  // text is returned unchanged so small drawings keep their full live
  // preview byte-for-byte. The marker is localised via `t` so it follows
  // the RU/EN toggle.
  const cappedDxfPreviewText = useMemo(
    () =>
      capDxfPreviewText(dxfPreviewText, (shown, total) =>
        t("dxfio.preview_truncated", { shown, total }),
      ),
    // `t` identity changes with the active language; recompute the marker
    // so the truncation note follows the RU/EN toggle. `dxfPreviewText`
    // is the only other input.
    [dxfPreviewText, t],
  );
  const documentTitleRef = useRef(geometryDocument.title);
  documentTitleRef.current = geometryDocument.title;
  const propertyRowsSnapshot = JSON.stringify(propertiesView.rows);
  const currentPrompt = buildDraftPrompt(draftSession, activeSpace.name);
  const acceptedInput = buildAcceptedInputText(draftSession);
  const commandHint = buildCommandHint(draftSession);
  const displayedCommandHint = draftSession
    ? commandHint
    : t("shell.default_hint");
  const deferredCommandText = useDeferredValue(commandText);
  const commandSuggestions = useMemo(
    () => buildCommandSuggestions(deferredCommandText, draftSession),
    [deferredCommandText, draftSession],
  );
  const draftPreviewEntity = buildDraftPreviewEntity(
    draftSession,
    state.propertyWorkflows.defaultObjectProperties,
  );
  const stageStatusText = buildStageStatusText(
    draftSession,
    activeSpace.name,
    activeEntitiesCount,
  );

  // ── Live canvas text input (TEXT/MTEXT/MLEADER content step) ──
  const isTextContentStep =
    draftSession &&
    (draftSession.step === "text-content" ||
      draftSession.step === "mtext-content" ||
      draftSession.step === "mleader-content");

  const prevStepRef = useRef<string | null>(null);
  if (draftSession?.step !== prevStepRef.current) {
    prevStepRef.current = draftSession?.step ?? null;
    if (isTextContentStep) {
      setLiveCanvasText("");
    }
  }

  const liveTextInputProp =
    isTextContentStep && draftSession
      ? {
          text: liveCanvasText,
          position: (() => {
            // MLEADER uses landing point (session.points[1])
            if (
              draftSession.step === "mleader-content" &&
              draftSession.points[1]
            ) {
              return {
                x: draftSession.points[1].x,
                y: draftSession.points[1].y,
              };
            }
            return {
              x: draftSession.points[0]?.x ?? 0,
              y: draftSession.points[0]?.y ?? 0,
            };
          })(),
          height: parseFloat(draftSession.options.height || "24") || 24,
          rotation: parseFloat(draftSession.options.rotation || "0") || 0,
          // AutoCAD parity (Phase B3): MLEADER content step opens a true
          // in-canvas MText editor with a bounding box at the landing
          // point, identical to MTEXT. See `docs/autocad-parity/mleader/`
          // — AutoCAD's MLEADER content stage shows a rectangular bbox
          // editor where the user types and presses Ctrl+Enter (Enter
          // here, since we don't have ribbon close-text-editor) to
          // commit. Single-line `<input>` was the v2 stop-gap; multi-
          // line `<textarea>` matches AutoCAD's actual UX.
          multiline:
            draftSession.step === "mtext-content" ||
            draftSession.step === "mleader-content",
          width:
            draftSession.step === "mtext-content" && draftSession.points[1]
              ? Math.abs(draftSession.points[1].x - draftSession.points[0].x)
              : draftSession.step === "mleader-content"
                ? // MLEADER: AutoCAD's default content bbox is ~80 mm
                  // wide at the leader landing — the user can resize
                  // post-create, but at first-edit the bbox needs SOME
                  // visible width or the textarea collapses.
                  120
                : undefined,
          onTextChange: (text: string) => {
            setLiveCanvasText(text);
            setCommandText(text);
          },
          onSubmit: () => {
            submitCommandTextRef.current(liveCanvasText);
            setLiveCanvasText("");
          },
          onCancel: () => {
            setDraftSession(null);
            setLiveCanvasText("");
            setCommandText("");
          },
        }
      : null;

  // ── In-place edit (double-click existing text entity) ──
  const inPlaceEditProp = inPlaceEditState
    ? (() => {
        const entity = state.drawing.entities.find(
          (e) => e.id === inPlaceEditState.entityId,
        );
        if (!entity) return null;
        const x = parseFloat(
          entity.properties["Insertion X"] ??
            entity.properties["Landing X"] ??
            "0",
        );
        const y = parseFloat(
          entity.properties["Insertion Y"] ??
            entity.properties["Landing Y"] ??
            "0",
        );
        const height = parseFloat(
          entity.properties.Height ?? entity.properties["Text height"] ?? "24",
        );
        const rotation = parseFloat(entity.properties.Rotation ?? "0");

        return {
          entityId: inPlaceEditState.entityId,
          text: inPlaceEditState.text,
          position: { x, y },
          height,
          rotation,
          multiline: entity.type === "MText",
          width:
            entity.type === "MText"
              ? parseFloat(entity.properties.Width ?? "500")
              : undefined,
          onTextChange: (text: string) =>
            setInPlaceEditState((prev) => (prev ? { ...prev, text } : null)),
          onSubmit: () => {
            if (inPlaceEditState) {
              dispatch({
                type: "properties/apply",
                source: "scene",
                entityIds: [inPlaceEditState.entityId],
                values: { Contents: inPlaceEditState.text },
              });
            }
            setInPlaceEditState(null);
          },
          onCancel: () => setInPlaceEditState(null),
        };
      })()
    : null;

  const parseBannerText = draftSession
    ? t("shell.parse_banner.active")
    : t("shell.parse_banner.idle");
  const workspaceSpaceBadge = isLayoutSpace
    ? state.drawing.layoutContext === "viewport"
      ? t("workspace.space_badge.viewport", { name: activeSpace.name })
      : t("workspace.space_badge.paper", { name: activeSpace.name })
    : t("workspace.space_badge.model", { name: activeSpace.name });
  const workspaceModeBadge = t("workspace.mode_badge", {
    command: draftSession
      ? draftSession.command
      : t("workspace.mode_badge.idle"),
  });
  const workspaceFlowBadge = draftSession
    ? t("workspace.flow_badge.active")
    : t("workspace.flow_badge.idle");
  const workspaceInputBadge = t("workspace.input_badge");
  const workspaceScopeBadge = t("workspace.scope_badge");

  useEffect(() => {
    // Phase 1.j (П35Р React #185 fix) — `setPropertyDraft` was firing
    // on every `propertyRowsSnapshot` identity change, including
    // cases where the actual content was identical. Object.fromEntries
    // always returns a new object reference → React tree re-render →
    // any selector returning fresh references → propertyRowsSnapshot
    // string recomputed but UNCHANGED → effect re-fires → loop. With
    // 685k entities the cumulative reconciliation pressure tripped
    // React error #185 ("Maximum update depth exceeded").
    //
    // Guard: skip the setter when the parsed rows produce the same
    // shape as current draft (functional setter returns prev ref).
    const nextRows = JSON.parse(propertyRowsSnapshot) as Array<{
      label: string;
      value: string;
    }>;
    setPropertyDraft((prev) => {
      const nextEntries = Object.fromEntries(
        nextRows.map((row) => [row.label, row.value]),
      );
      // Shallow-equal check — same keys, same values → keep prev ref.
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(nextEntries);
      if (prevKeys.length === nextKeys.length) {
        let equal = true;
        for (const k of nextKeys) {
          if (prev[k] !== nextEntries[k]) {
            equal = false;
            break;
          }
        }
        if (equal) return prev;
      }
      return nextEntries;
    });
  }, [propertyRowsSnapshot]);

  // Re-audit P1 fix: mount the drawing-aid sysvar hotkeys (F7/F8/F9/F10/F12 +
  // Shift / Shift+letter OSNAP temp-overrides). `useSysvarHotkeys` installs the
  // window-level keydown/keyup listeners — it was defined and barrel-exported
  // but NEVER mounted in any production component, so every F-key toggle and
  // the entire Shift temp-override OSNAP subsystem (OSNAP-TEMPOVERRIDE-WIRE
  // #346) were dead code in the live app. Unit tests passed because they drove
  // `handleSysvarKeyDown/Up` directly, bypassing the never-installed listener.
  useSysvarHotkeys();

  useKeyboardShortcuts({
    commandText,
    draftSession,
    selectedEntities,
    lastSubmittedCommand: state.commandLine.lastSubmitted,
    exportedDxfTextRef,
    documentTitleRef,
    submitCommandTextRef,
    beginDraftingRef,
    dispatch,
    setDraftSession,
    setCommandText,
    setCommandHistoryOpen,
    appendCommandFeed,
    // CECOLOR round-8 A / round-9 ROOT CURE — let Escape cancel the colour-input
    // context (the `colorPromptPending` boolean keeps the early-return guard
    // aware a prompt is active; the shared clearer does the teardown).
    colorPromptPending,
    clearColorTransientState,
  });

  function openPanel(panel: PanelId) {
    // cmd-pan P2-4 — a panel-open is a direct navigation entry point that
    // bypasses submitCommandText; supersede an active realtime-PAN first so the
    // session never sticks (hand cursor + next-LMB-eaten-as-pan).
    supersedePanSession();
    expandInspector();
    // CC-1e — non-command navigation telemetry (panel switch). Emitted
    // alongside the existing command so the §14 "clicks" axis records the UI
    // interaction; observability only, does not alter the dispatch below.
    useEditorStore.getState().recordUiNav({
      navKind: "panel",
      from: state.ui.activePanel,
      to: panel,
    });
    dispatch({
      type: "shell/open-panel",
      source: "toolbar",
      panel,
    });
  }

  function activateSpace(
    spaceId: string,
    layoutContext?: "model" | "paper" | "viewport",
  ) {
    // cmd-pan P2-4 — switching the active space/layout from a tab is a direct
    // entry point that bypasses submitCommandText; end an active realtime-PAN
    // first (the pan belonged to the OLD space's camera).
    supersedePanSession();
    if (spaceId !== state.drawing.activeSpaceId) {
      dispatch({
        type: "shell/activate-space",
        source: "toolbar",
        spaceId,
        layoutContext,
      });
      return;
    }

    if (layoutContext && layoutContext !== "model") {
      dispatch({
        type: "shell/set-layout-context",
        source: "toolbar",
        layoutContext,
      });
    }
  }

  // B2 — begin inline tab rename. AutoCAD parity: Model space can never
  // be renamed, so double-clicking the Model tab is a no-op. Populates
  // the draft name with the current name so the user can edit in place.
  function beginRenameSpace(spaceId: string) {
    if (spaceId === "model") {
      return;
    }
    const space = state.drawing.spaces.find((s) => s.id === spaceId);
    if (!space) {
      return;
    }
    setRenamingSpaceId(spaceId);
    setRenamingDraftName(space.name);
  }

  // B2 — commit the inline tab rename. Performs the same validations as
  // the CLI path so UX stays consistent: empty/whitespace-only → cancel
  // silently; same-name → cancel; duplicate → audit warning; otherwise
  // dispatch `shell/rename-space` and close the editor. The reducer is
  // the source of truth for the final guard set — the UI layer just
  // catches obviously-invalid input early to keep the feedback channel
  // focused on real conflicts.
  function commitRenameSpace() {
    const spaceId = renamingSpaceId;
    if (!spaceId) {
      return;
    }
    const trimmed = renamingDraftName.trim();
    const current = state.drawing.spaces.find((s) => s.id === spaceId);
    // Clear the editor regardless of validation outcome.
    setRenamingSpaceId(null);
    setRenamingDraftName("");
    if (!current) {
      return;
    }
    if (!trimmed || trimmed === current.name) {
      return;
    }
    // Shared guard (spaceMutationGuards.ts) — same CI duplicate + length
    // predicate as both reducer arms (Codex P2-2 / P2-4). A pure case
    // change of the tab's own name passes (AutoCAD permits it).
    const inlineRenameRejection = validateRenameSpace(
      state.drawing.spaces,
      spaceId,
      trimmed,
    );
    if (inlineRenameRejection !== null) {
      appendCommandFeed(
        inlineRenameRejection === "duplicate-name"
          ? t("layout.rename.duplicate_name", { name: trimmed })
          : inlineRenameRejection === "name-too-long"
            ? t("layout.name.too_long")
            : t("layout.rename.cannot_rename_model"),
      );
      return;
    }
    dispatch({
      type: "shell/rename-space",
      source: "scene",
      spaceId,
      newName: trimmed,
    });
    appendCommandFeed(
      t("layout.rename.success", { old: current.name, new: trimmed }),
    );
  }

  function cancelRenameSpace() {
    setRenamingSpaceId(null);
    setRenamingDraftName("");
  }

  // CECOLOR round-8 FINDING D — wire the keyboard-alias-key draft-start path
  // (useKeyboardShortcuts → `beginDraftingRef.current(draftCommand)`, e.g.
  // pressing "L" for LINE) through the colour-reset wrapper too, so EVERY
  // draft-command start (ribbon / typed / queued / keyboard alias) clears a
  // still-armed `-COLOR` pending prompt.
  beginDraftingRef.current = beginDraftingWithColorReset;

  function queueCommand(text: string) {
    const draftCommand = resolveShellDraftCommand(text);

    if (draftCommand) {
      beginDraftingWithColorReset(draftCommand);
      return;
    }

    setCommandText(text);
  }

  // D5 (App.tsx router→registry unification) — the ~1500-line inline
  // command-line router was extracted to `commandDispatch/` (registry-driven
  // dispatch, no literal `upperStart === "ALIAS"` comparisons). `deps`
  // bundles every render-scoped value the extracted handlers close over;
  // rebuilt fresh every render, mirroring the original inline function's
  // own "redefined every render" lifetime.
  const submitCommandText = createSubmitCommandText({
    dispatch,
    state,
    geometryDocument,
    selectedEntities,
    appendCommandFeed,
    setCommandText,
    t,
    draftSession,
    handleDraftTextSubmission,
    beginDraftingWithColorReset,
    colorPromptPending,
    setColorPromptPending,
    setCeColorPickerOpen,
    panSessionActiveRef,
    endPanSession,
    startPanSession,
    stageCameraApiRef,
    renderSignalResolversRef,
    submitCommandTextRef,
    setWriteBlockDialogOpen,
    openPurgeDialog,
    setRenameBlockDialogOpen,
    setLayerManagerOpen,
    printDialog,
    pageSetupDialog,
    scaleListEditDialog,
    bpmDialog,
    eattextDialog,
    xrefDialog,
    battmanDialog,
    blockAuthoringShell,
    setLookupTableDialog,
    setBlockPropertiesDialogOpen,
    historyBatchDepth,
    // W2.5 — DSETTINGS/DDRMODES command text still opens on the plain
    // "dynamic-input" default (its pre-existing behaviour); routed through
    // the single `openDsettingsOnTab` choke point so it can never inherit a
    // tab left over from the Osnap override menu's own opener.
    setDsettingsOpen: (open) => {
      if (open) openDsettingsOnTab("dynamic-input");
    },
    openSpecialEditor: specialEditor.open,
  });

  submitCommandTextRef.current = submitCommandText;

  // Right-click context menu (AutoCAD SHORTCUTMENU parity) is owned by
  // `useStageContextMenu` (Track 3 Phase 7). The hook call is placed
  // below the build-context-menu function-extraction marker so it has
  // access to `submitCommandTextRef`, `draftSession`, and `dispatch`.

  // AutoCAD ROLLOVERTIPS placement — mirror cursor position into the
  // hover store so ToolTipCard can anchor near the cursor. Pure passthrough;
  // PREVIEWFILTER is applied on the entity-id channel
  // (handleStageHover in useDraftSession).
  function handleStageCursorClient(
    cursor: { x: number; y: number } | null,
  ): void {
    const store = stageHoverStore.getState();
    if (cursor === null) {
      // Pointer left the canvas — fully clear so the tooltip hides.
      store.clear();
      return;
    }
    store.setHover(store.entityId, cursor, store.spaceId);
  }

  // Right-click stage context menu — owned by `useStageContextMenu`
  // (Track 3 Phase 7). Returns the open-flag, the right-click handler,
  // the close callback, and the per-mode item builder. Replaces the
  // ~330-line inline block (state + handler + builder) that lived
  // here pre-Phase 7.
  const {
    stageContextMenu,
    handleStageContextMenu,
    closeStageContextMenu,
    buildContextMenuItems,
  } = useStageContextMenu({
    state,
    draftSession,
    t,
    submitCommandTextRef,
    dispatch,
    // cmd-pan r6 P2-1 — the menu wraps EVERY item action (except 'Pan') through
    // this so a direct-`dispatch` item (deselect-all / properties) chosen while
    // a realtime PAN is active tears the session down first (no stuck pan).
    supersedePanSession,
    // W2.5 OSNAP-OVERRIDE-MODES-S2 — the "osnap" mode's "Osnap Settings..."
    // item opens the same DSETTINGS dialog `DynInputOverlay`'s
    // `onOpenSettings` prop already triggers, but landed on the Object Snap
    // TAB specifically (local Codex round-2 P2 — `DSettingsDialog` defaults
    // to "dynamic-input", which is the correct default for every OTHER
    // opener but not this one).
    onOpenOsnapSettings: () => openDsettingsOnTab("osnap"),
  });

  // ============================================================
  // AI_BATCH_COMMANDS (Variant A)
  // Execute a sequence of command-line entries, waiting for React
  // to actually commit each re-render before moving to the next
  // command. This works regardless of how long a render takes —
  // fast commands pass through instantly, slow commands wait
  // as long as needed.
  //
  // Mechanism:
  //   1. useLayoutEffect runs after every React commit.
  //   2. It drains `renderSignalResolversRef.current`, resolving
  //      all pending waiters.
  //   3. Batch runner pushes a resolver into the ref and awaits
  //      the promise — it wakes up exactly when the re-render
  //      triggered by the previous command has finished.
  //   4. A 300ms fallback timeout prevents hangs when a command
  //      doesn't actually trigger a state change.
  //
  // Usage from DevTools or AI scripts:
  //   window.editorRunBatch(["LINE","0,0","5000,0",""])
  //     .then(results => console.table(results));
  // ============================================================
  // (`renderSignalResolversRef` itself is declared earlier — see D5 note
  // near `submitCommandTextRef` above.)

  useLayoutEffect(() => {
    // React has just committed a render — wake up any waiters.
    const resolvers = renderSignalResolversRef.current;
    renderSignalResolversRef.current = [];
    resolvers.forEach((resolve) => resolve());
  });

  // ── editorRunBatch: exposed on window for E2E / AI / DevTools ──
  // Defined as a stable ref-based function that survives React
  // re-renders and StrictMode double-mount. Previous implementation
  // used useEffect with [] deps, but its cleanup deleted the global
  // between unmount→remount, causing "editorRunBatch is not a function"
  // errors during multi-step batch sequences (PLINE, HATCH, BLOCK).
  type BatchResult = {
    index: number;
    text: string;
    ok: boolean;
    error?: string;
  };

  const runBatchRef = useRef<
    ((commands: string[]) => Promise<BatchResult[]>) | null
  >(null);

  if (!runBatchRef.current) {
    const waitForNextRender = () =>
      new Promise<void>((resolve) => {
        let settled = false;
        const wrapped = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        renderSignalResolversRef.current.push(wrapped);
        setTimeout(wrapped, 300);
      });

    runBatchRef.current = async (
      commands: string[],
    ): Promise<BatchResult[]> => {
      const results: BatchResult[] = [];
      for (let i = 0; i < commands.length; i++) {
        const text = commands[i];
        try {
          submitCommandTextRef.current?.(text, "batch");
          results.push({ index: i, text, ok: true });
        } catch (error) {
          results.push({
            index: i,
            text,
            ok: false,
            error: error instanceof Error ? error.message : String(error),
          });
        }
        await waitForNextRender();
      }
      return results;
    };
  }

  // Assign on every render so it's always available (no cleanup to
  // remove it). React StrictMode double-mount is harmless because
  // the ref is stable.
  (window as unknown as Record<string, unknown>).editorRunBatch =
    runBatchRef.current;

  // Phase J.3 — switch ribbon mode based on the active space. When
  // BEDIT is active, hide normal tabs and surface the block-editor
  // tab. The selector also feeds the contextual ribbon section
  // (J.2) when exactly one BlockReference is selected.
  const isInBlockEdit = geometryDocument.activeSpaceId === BLOCK_EDIT_SPACE_ID;
  const visibleRibbonTabIds: readonly RibbonTabId[] = isInBlockEdit
    ? blockEditorRibbonTabIds
    : ribbonTabIds;
  // Phase J.2 — surface "Контекст блока" section when exactly one
  // BlockReference is selected (mirrors AutoCAD's contextual tab).
  const ribbonSelectionContext = (() => {
    if (state.selection.entityIds.length !== 1) return undefined;
    const onlySelected = selectedEntities[0];
    if (!onlySelected || onlySelected.type !== "Block") return undefined;
    const nameRaw = onlySelected.properties["Name"];
    const name = typeof nameRaw === "string" ? nameRaw : undefined;
    return {
      isSingleBlockReference: true,
      selectedBlockName: name,
    };
  })();

  const ribbonSections = buildRibbonSections(
    {
      t: t as (key: string) => string,
      beginDrafting: beginDraftingWithColorReset,
      openPanel,
      activateSpace,
      dispatch,
      // cmd-pan P2-4 — these DIRECT ribbon callbacks bypass submitCommandText /
      // beginDraftingWithColorReset, so each is wrapped in `withPanSupersede` to
      // tear down an active realtime-PAN before the action runs (else the prompt
      // + hand cursor stick and the next canvas LMB is eaten as a pan).
      onImportDxf: withPanSupersede(() => dxfImportInputRef.current?.click()),
      resetShellState,
      // "Файл" / "File" ribbon → Print button. Opens the same
      // page-setup dialog reached by PRINT / PLOT / ПЕЧАТЬ at the
      // command line (the usePrintDialog hook owns the overlay state).
      onOpenPrint: withPanSupersede(() => printDialog.open()),
      // "Файл" / "File" ribbon → Page Setup button (PDF-6.9a). Opens
      // the per-layout Page Setup Manager — same entry point as
      // typing PAGESETUP / ПАРАМЛИСТ at the command line.
      onOpenPageSetup: withPanSupersede(() => pageSetupDialog.open()),
      // Phase K.1 — open the floating Block Authoring Palette shell on
      // the requested tab. The "visibility" pane has no dedicated tab
      // in the shell — Phase K.4 routes that ribbon click to the
      // existing DYN-1.7 Block Authoring Palette so the Visibility
      // States Manager dialog opens automatically (matching the BVSTATE
      // command-line behaviour).
      // cmd-pan P2-4 — also a DIRECT bypass (opens the palette/dialog without
      // routing through submitCommandText): supersede an active PAN first.
      onOpenBlockAuthoringPalette: withPanSupersede((pane) => {
        if (pane === "visibility") {
          // Phase K.4 — the "Видимость" ribbon button opens the
          // Visibility States dialog directly via the DYN-1.7 palette
          // open-request mechanism. Same path as BVSTATE typed at the
          // command line. If the active block has no visibility
          // parameter, the palette surfaces an explanatory toast (via
          // onNoVisibilityParameter).
          blockAuthoringShell.palette.requestOpen("visibility");
          return;
        }
        const tab: BlockAuthoringPaletteTab =
          pane === "actions"
            ? "actions"
            : pane === "parameter-sets"
              ? "parameter-sets"
              : "parameters";
        blockAuthoringShell.shell.openOnTab(tab);
      }),
    },
    isInBlockEdit,
    ribbonSelectionContext,
  );

  function submitCommand(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submitCommandText(commandText);
  }

  function submitProperties(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    dispatch({
      type: "properties/apply",
      source: "toolbar",
      entityIds: state.selection.entityIds,
      values: propertyDraft,
    });
  }

  // AutoCAD parity: properties auto-apply on blur / Enter / dropdown change.
  // Uses a ref to always capture the latest propertyDraft at call time.
  const propertyDraftRef = useRef(propertyDraft);
  propertyDraftRef.current = propertyDraft;

  // Refs for Ctrl+S / Ctrl+A keyboard shortcuts (avoids stale closures).
  function autoApplyProperties() {
    dispatch({
      type: "properties/apply",
      source: "toolbar",
      entityIds: state.selection.entityIds,
      values: propertyDraftRef.current,
    });
  }

  // B15 — extracted import path so both <input type="file"> and the
  // drag-and-drop drop-zone go through the same async pipeline. This
  // is the post-V8 / V9 standard "single async import boundary" the
  // existing dxfWorkerClient assumes — never duplicate the
  // `importDxfFileAsyncReplacing` + DYN-hydration sequence inline.
  async function importDxfFileWithHydration(file: File): Promise<void> {
    try {
      // Pass the File handle directly into the worker — the decode
      // (`file.text()`) happens inside the worker thread, so the giant
      // source string never lives on the main thread. Critical for
      // 100–500 MB DXF files that would OOM the main thread on decode
      // or on structured clone of the source into the worker.
      const result = await importDxfFileAsyncReplacing(file);
      setDxfImportStatus(
        result.ok
          ? result.message
          : t("dxfio.status.failed", { message: result.message }),
      );
      // Phase 4 DYN* hydration: after a successful import, read any $DYNMODE /
      // $DYNDIGRIP / … variables stored in the DXF HEADER section and apply
      // them to the sysvar store. Falls back to localStorage / DYN_DEFAULTS
      // when the document has no DYN* vars (legacy DXF parity).
      if (result.ok) {
        const currentDoc = useEditorStore.getState().geometryDocument;
        if (currentDoc) {
          // Hydrate the live sysvar store FROM the loaded document's HEADER vars
          // (DYN* family + the typed MIRRTEXT / OSMODE fields) so a subsequent
          // SETVAR / MIRROR honours the file's settings AND — critically — so the
          // export projection (`applyLiveHeaderSysvars`) is a genuine no-op until
          // a real SETVAR (NEW-1: without OSMODE here, the first save corrupted
          // $OSMODE). The hydrate-in / project-out seam lives in ONE module
          // (`dxfGeometry.ts`) so the two directions can't drift apart.
          const hydrateVars = buildHeaderSysvarHydration(currentDoc);
          if (Object.keys(hydrateVars).length > 0) {
            useSysvarStore.getState().hydrateFromDxf(hydrateVars);
          }
          // CECOLOR — hydrate the live `cecolorStore` FROM the file's $CECOLOR
          // (union sibling of the sysvar hydration), so a COLOR / save sees the
          // loaded value and the export project-out (`applyLiveCeColor`) is a
          // no-op until a real COLOR. The merged doc already preserved the live
          // colour on INSERT (`mergeCeColor`), so this is a no-op there.
          useCeColorStore
            .getState()
            .hydrateCeColor(buildCecolorHydration(currentDoc));
          // CECOLOR round-9 FINDING C — a replace-import (this SINGLE open path —
          // file-picker AND drag-drop) hydrates the live colour FROM the file but
          // does NOT clear the transient colour-input UI: a still-open COLOR
          // picker / `-COLOR` prompt / ribbon swatch picker would otherwise stay
          // mounted over the freshly-opened drawing and apply/consume the next
          // colour action against it. Mirror the reset path — close the
          // command-line picker, clear the pending `-COLOR` flag, and bump the
          // swatch reset epoch — via the shared clearer (same teardown as every
          // other seam).
          clearColorTransientState();
        }
      }
    } catch (error) {
      setDxfImportStatus(
        t("dxfio.status.failed", {
          message: error instanceof Error ? error.message : String(error),
        }),
      );
    }
  }

  async function handleDxfFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    try {
      await importDxfFileWithHydration(file);
    } finally {
      event.target.value = "";
    }
  }

  // ── B15 DXF drag-and-drop drop-zone ───────────────────────────────
  // AutoCAD parity: dragging a .dxf onto the workspace imports it the
  // same way the file-picker button does. We add the listeners on the
  // <main> element so the drop-zone covers the canvas + its sibling
  // panels — but the visual indicator is overlaid only over the
  // drawing-area-flex column to avoid distracting on side panels.
  //
  // Counter-based enter/leave so nested children don't flicker the
  // active state. The HTML dragenter/dragleave events bubble for every
  // descendant: enter→counter++, leave→counter--, drop→reset+import.
  const [dropZoneActive, setDropZoneActive] = useState(false);
  const dropZoneCounterRef = useRef(0);
  const [dropZoneError, setDropZoneError] = useState<string | null>(null);

  const handleWorkspaceDragOver = useCallback(
    (event: ReactDragEvent<HTMLElement>) => {
      // preventDefault on dragover is REQUIRED by the HTML5 DnD spec —
      // without it the browser refuses to fire `drop` (default action
      // = "none"). Always indicate the drop is allowed; type filtering
      // happens on `drop`.
      if (event.dataTransfer.types.includes("Files")) {
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      }
    },
    [],
  );

  const handleWorkspaceDragEnter = useCallback(
    (event: ReactDragEvent<HTMLElement>) => {
      if (!event.dataTransfer.types.includes("Files")) return;
      dropZoneCounterRef.current += 1;
      if (!dropZoneActive) setDropZoneActive(true);
      setDropZoneError(null);
    },
    [dropZoneActive],
  );

  const handleWorkspaceDragLeave = useCallback(
    (_event: ReactDragEvent<HTMLElement>) => {
      // Counter-based: only deactivate when ALL nested elements have
      // also fired dragleave. Otherwise hovering across a child border
      // would flicker the indicator off-on.
      dropZoneCounterRef.current = Math.max(0, dropZoneCounterRef.current - 1);
      if (dropZoneCounterRef.current === 0) {
        setDropZoneActive(false);
      }
    },
    [],
  );

  const handleWorkspaceDrop = useCallback(
    (event: ReactDragEvent<HTMLElement>) => {
      event.preventDefault();
      dropZoneCounterRef.current = 0;
      setDropZoneActive(false);
      const file = event.dataTransfer.files?.[0];
      if (!file) return;
      // AutoCAD only accepts .dxf via drag-and-drop (.dwg via dwg2dxf
      // converter). Reject non-DXF with a status message instead of
      // silently failing — the file-picker has the same .dxf-only gate.
      const lowerName = file.name.toLowerCase();
      if (!lowerName.endsWith(".dxf")) {
        setDropZoneError(
          t("dxfio.drop_zone_invalid_type", { name: file.name }),
        );
        return;
      }
      setDropZoneError(null);
      void importDxfFileWithHydration(file);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  function resetShellState() {
    reset();
    // CECOLOR (round-8 FINDING B) — `reset()` rebuilds a fresh ByLayer geometry
    // document (`doc.cecolor` = ByLayer, the per-drawing template default), but
    // the live `cecolorStore` still holds the prior current colour; without this
    // mirror, stampCeColorOntoCreateCommand + the export project-out
    // (`applyLiveCeColor`) would stamp the stale colour into the brand-new ByLayer
    // drawing, AND the live value would DISAGREE with `doc.cecolor`. Mirror the
    // live value back to ByLayer so doc and live AGREE on new/reset (AutoCAD's NEW
    // resets CECOLOR to the new drawing's template default ByLayer — CECOLOR is a
    // per-drawing sysvar, not a cross-session profile; there is no localStorage
    // preference to preserve — round-8 dropped the non-AutoCAD persist).
    useCeColorStore.getState().hydrateCeColor({ ...DEFAULT_CE_COLOR });
    setDraftSession(null);
    setLiveCanvasText("");
    setInPlaceEditState(null);
    setCommandText("");
    clearCommandFeed();
    setCommandHistoryOpen(false);
    // CECOLOR (FINDING 5 / round-8 B / round-9 ROOT CURE) — end the colour-input
    // context: close the COLOR "Select Color" picker AND the dialog-less `-COLOR`
    // pending-reply flag AND the ribbon swatch's LOCAL picker (via the store
    // reset epoch the swatch subscribes to — its open-state is component-local
    // and out of reach of the command-line picker close). Routed through the
    // shared clearer so reset uses the SAME teardown as every other seam.
    clearColorTransientState();
    // cmd-pan FINDING 1 — file-open / NEW must also clear an active realtime-pan
    // session so the prompt strip + hand cursor do not persist into the fresh
    // drawing (same single-seam supersede pattern as the colour-transient end).
    // cmd-pan P3-6 — tear it down SILENTLY: the feed was just cleared above
    // (`clearCommandFeed()`), so a non-silent end would re-append `PAN: panning
    // ended.` and leave a stale line in the reset drawing.
    if (panSessionActiveRef.current) endPanSession({ silent: true });
    setStageResetToken((currentToken) => currentToken + 1);
    setDxfImportStatus(t("dxfio.status.default"));
    blockDefinitionDialog.close();
    insertBlockDialog.close();
    setWriteBlockDialogOpen(false);
    setPurgeBlocksDialogOpen(false);
    setRenameBlockDialogOpen(false);
    setPurgeCandidates([]);
    // PR-9 — new annotative dialogs (Track 3 Phase 4 — owned by hooks).
    scaleListEditDialog.close();
    annotativeObjectScaleDialog.close();
    // Track 3 Phase 4 — XREF / PRINT dialog state owned by hooks.
    xrefDialog.close();
    printDialog.close();
    // PDF-6.9a — PAGESETUP dialog state owned by hook.
    pageSetupDialog.close();
    // PDF-6.9b — PDF Options sub-dialog state owned by hook.
    pdfOptionsDialog.close();
    // Track 3 Phase 6 — ATTDEF / DDATTE / EATTEDIT closure delegated to hook.
    attributeDialogs.closeAll();
    // Track 3 Phase 5 — BPM / BATTMAN / EATTEXT dialog state owned by hooks.
    bpmDialog.close();
    battmanDialog.close();
    eattextDialog.close();
  }

  // ── ATTDEF / DDATTE / EATTEDIT auto-open ──
  // Track 3 Phase 6 — moved into `useAttributeDialogs`. Track 3
  // Phase 2 — BLOCK / INSERT auto-open moved into
  // `useBlockDefinitionDialog` / `useInsertBlockDialog`. BCLOSE was
  // moved into `useBlockEditExitDialog` (Track 3 Phase 1).

  // Phase J.3 ribbon-tab auto-switch — moved into `useRibbonTabState`
  // (Track 3 Phase 7). The hook now owns the BEDIT-boundary effect.


  // ── BLOCK / INSERT / Overlap warning handlers ──
  // Track 3 Phase 2 — moved into `useBlockDefinitionDialog`,
  // `useInsertBlockDialog`, and `useOverlapWarningDialog` respectively.
  // The dialogs below consume each hook's `props` bundle directly.

  // ── WBLOCK handlers (Phase 2.6) ──────────────────────────────
  function handleWriteBlockConfirm(params: WriteBlockParams) {
    try {
      const source =
        params.source === "block"
          ? { kind: "block" as const, blockName: params.blockName ?? "" }
          : params.source === "drawing"
            ? { kind: "drawing" as const }
            : {
                kind: "objects" as const,
                entityIds: params.selectedEntityIds ?? [],
                basePoint: params.basePoint ?? { x: 0, y: 0 },
              };
      const result = wblockExportMutation({
        source,
        filename: params.filename,
        insertionUnits: params.insertionUnits,
        drawingDocument: geometryDocument,
      });

      // Trigger browser download via anchor + Blob. No reducer dispatch —
      // WBLOCK is a pure side-effect (file export).
      if (typeof window !== "undefined" && typeof document !== "undefined") {
        const blob = new Blob([result.dxfText], {
          type: "application/dxf",
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = result.suggestedFilename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        // Revoke URL after a short delay to give the browser time to
        // start the download — matches common Blob-download patterns.
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      }

      appendCommandFeed(
        t("summary.wblock_exported", {
          filename: result.suggestedFilename,
        }),
      );
      setWriteBlockDialogOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      appendCommandFeed(t("toast.wblock.failed", { message }));
    }
  }

  function handleWriteBlockCancel() {
    setWriteBlockDialogOpen(false);
  }

  // ── PURGE handlers (Phase 2.7) ──────────────────────────────
  function openPurgeDialog() {
    const analysis = purgeBlocksMutation({
      drawingDocument: geometryDocument,
    });
    setPurgeCandidates(analysis.candidates);
    setPurgeBlocksDialogOpen(true);
  }

  function handlePurgeBlocksConfirm(blockNames: string[]) {
    try {
      dispatch({
        type: "file/purge-blocks",
        source: "commandline",
        blockNames,
      });
      appendCommandFeed(
        t("summary.purge_completed", { count: blockNames.length }),
      );
      setPurgeBlocksDialogOpen(false);
      setPurgeCandidates([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      appendCommandFeed(message);
    }
  }

  function handlePurgeBlocksCancel() {
    setPurgeBlocksDialogOpen(false);
    setPurgeCandidates([]);
  }

  // ── XREF handlers (Phase FIN.3) ─────────────────────────────
  // Owned by `useXrefDialog` (Track 3 Phase 4). The hook bundles the
  // file-picker → DXF parse → `file/x*` dispatch round-trips, plus
  // the manager onClose callback, into `xrefDialog.props`.

  // ── RENAME handlers (Phase 2.8) ──────────────────────────────
  function validateRenameForDialog(oldName: string, newName: string) {
    return validateRenameBlockCore(oldName, newName, geometryDocument);
  }

  function handleRenameBlockConfirm(params: {
    oldName: string;
    newName: string;
  }) {
    try {
      dispatch({
        type: "file/rename-block",
        source: "commandline",
        oldName: params.oldName,
        newName: params.newName,
      });
      appendCommandFeed(
        t("summary.rename_completed", {
          oldName: params.oldName,
          newName: params.newName,
          count: 0,
        }),
      );
      setRenameBlockDialogOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      appendCommandFeed(t("toast.rename.failed", { message }));
    }
  }

  function handleRenameBlockCancel() {
    setRenameBlockDialogOpen(false);
  }

  // ── BATTMAN / BPM handlers ───────────────────────────────────
  // Track 3 Phase 5 — moved into `useBattmanDialog` / `useBpmDialog`
  // alongside the open/close state. Both hooks expose imperative
  // `open()` / `close()` plus the dialog props bundle.

  // ── ATTDEF / DDATTE / EATTEDIT handlers ─────────────────────
  // Track 3 Phase 6 — moved to
  // `apps/web/src/app/hooks/useAttributeDialogs.ts`. The hook owns
  // each Confirm / Cancel / Apply handler plus the open flags. The
  // resolver below stays in App.tsx because it depends on
  // `selectedEntities` + `draftSession.options` which are still
  // App-scoped (reachable via `draft` and `selectSelectedEntities`).
  function resolveSelectedInsertForAttributeEditing(): {
    insert: BlockInsertEntityRuntime;
    blockDef: BlockDefinitionRuntime;
  } | null {
    const targetEntityId = draftSession?.options?.targetEntityId;
    const candidateId =
      targetEntityId ??
      selectedEntities.find((e) => e.type === "Block")?.id ??
      null;
    if (!candidateId) return null;
    for (const space of geometryDocument.spaces) {
      const candidate = space.entities.find((e) => e.id === candidateId);
      if (candidate && candidate.type === "Block") {
        const blockDef = geometryDocument.blocks[candidate.blockName];
        if (!blockDef) return null;
        return { insert: candidate, blockDef };
      }
    }
    return null;
  }

  // ── Phase J.5 — BlockEditExitDialog handlers ──
  // Moved to `apps/web/src/app/hooks/useBlockEditExitDialog.ts` as part
  // of Track 3 Phase 1 (App.tsx decomposition). The hook owns the
  // Save / Discard / Cancel handlers, the auto-open effect, and the
  // local open flag — App.tsx now consumes `blockEditExitDialog.props`
  // when rendering the dialog below.

  return (
    <div className="app-shell" data-testid="app-shell">
      <div className="chrome-glow chrome-glow-left" aria-hidden="true" />
      <div className="chrome-glow chrome-glow-right" aria-hidden="true" />

      <EditorHeader backendRuntime={backendRuntime} />

      <section className="ribbon-shell" data-testid="editor-ribbon">
        <div className="ribbon-topline">
          <nav
            className="ribbon-tabbar"
            aria-label={t("header.editor_ribbon_aria")}
          >
            {visibleRibbonTabIds.map((tabId) => (
              <button
                key={tabId}
                className={`ribbon-tab ${activeRibbonTab === tabId ? "is-active" : ""}`}
                type="button"
                data-testid={`ribbon-tab-${tabId}`}
                onClick={() => {
                  // CC-1e — ribbon-tab switch navigation telemetry.
                  useEditorStore.getState().recordUiNav({
                    navKind: "ribbon-tab",
                    from: activeRibbonTab,
                    to: tabId,
                  });
                  setActiveRibbonTab(tabId);
                }}
              >
                {t(`ribbon.tab.${tabId}` as never)}
              </button>
            ))}
          </nav>

          <div className="ribbon-quickbar">
            <span className="mini-badge">{t("header.compact_ribbon")}</span>
            {/* Phase 4.1.b — Undo / Redo quickbar buttons */}
            <button
              type="button"
              className="mini-badge"
              data-testid="quickbar-undo"
              disabled={historyPastLength === 0}
              title={t("quickbar.undo.tooltip" as never)}
              onClick={() => {
                // CC-1e — quickbar click telemetry (alongside the command).
                useEditorStore
                  .getState()
                  .recordUiClick({ target: "quickbar", controlId: "quickbar-undo" });
                dispatch({ type: "history/undo", source: "toolbar" });
              }}
            >
              {t("quickbar.undo" as never)}
            </button>
            <button
              type="button"
              className="mini-badge"
              data-testid="quickbar-redo"
              disabled={historyFutureLength === 0}
              title={t("quickbar.redo.tooltip" as never)}
              onClick={() => {
                // CC-1e — quickbar click telemetry (alongside the command).
                useEditorStore
                  .getState()
                  .recordUiClick({ target: "quickbar", controlId: "quickbar-redo" });
                dispatch({ type: "history/redo", source: "toolbar" });
              }}
            >
              {t("quickbar.redo" as never)}
            </button>
            <span className="mini-badge">{activeSpace.name}</span>
            {(() => {
              // Phase 2.3 — Block Editor active indicator. Surfaces the
              // current block name in the quickbar so the user always
              // knows the drawing canvas is a BEDIT session rather than
              // the regular model / layout space.
              const activeGeo = geometryDocument.spaces.find(
                (s) => s.id === geometryDocument.activeSpaceId,
              );
              if (activeGeo?.kind !== "block-edit") return null;
              return (
                <span
                  className="mini-badge drawing-stage-refedit-tint"
                  data-testid="status-bedit-indicator"
                  title={t("status.bedit.editing", {
                    name: activeGeo.blockEditName ?? "",
                  })}
                >
                  {t("status.bedit.editing", {
                    name: activeGeo.blockEditName ?? "",
                  })}
                </span>
              );
            })()}
            {/* Phase FIN.1 — REFEDIT active indicator. Track 3 Phase 5
                — owned by `useRefEditWorkflow`. */}
            {refEditWorkflow.statusIndicator}
            {/* Phase FIN.2 — CANNOSCALE selector for annotative blocks. */}
            <span
              className="ribbon-context-area"
              data-testid="quickbar-cannoscale"
            >
              <span className="context-label">{t("cannoscale.selector")}</span>
              <select
                className="context-select"
                data-testid="quickbar-cannoscale-select"
                value={geometryDocument.cannoscaleName}
                onChange={(event) => {
                  dispatch({
                    type: "header/set-cannoscale",
                    source: "scene",
                    cannoscaleName: event.target.value,
                  });
                }}
              >
                {((geometryDocument.annotationScales ?? []).length > 0
                  ? (geometryDocument.annotationScales ?? [])
                  : [
                      { name: "1:1", scaleFactor: 1 },
                      { name: "1:2", scaleFactor: 2 },
                      { name: "1:5", scaleFactor: 5 },
                      { name: "1:10", scaleFactor: 10 },
                      { name: "1:20", scaleFactor: 20 },
                      { name: "1:50", scaleFactor: 50 },
                      { name: "1:100", scaleFactor: 100 },
                    ]
                ).map((scale) => (
                  <option key={scale.name} value={scale.name}>
                    {scale.name}
                  </option>
                ))}
              </select>
            </span>
            <span className="mini-badge">
              {t("ribbon.quickbar.live_entities", {
                count: geometryDocument.spaces.reduce(
                  (count, space) => count + space.entities.length,
                  0,
                ),
              })}
            </span>
            <button
              type="button"
              className="mini-badge coordinate-system-toggle"
              data-testid="coordinate-system-toggle"
              title={
                coordinateSystem === "cad"
                  ? t("ribbon.coord_toggle.cad_title")
                  : t("ribbon.coord_toggle.svg_title")
              }
              onClick={toggleCoordinateSystem}
            >
              Y {coordinateSystem === "cad" ? "↑" : "↓"}
            </button>
            {/* Always-visible quickbar dropdowns: Layer, DimStyle, MLeaderStyle */}
            <span className="ribbon-context-area" data-testid="quickbar-layer">
              <button
                className="context-link-button"
                type="button"
                onClick={withPanSupersede(() => setLayerManagerOpen(true))}
              >
                {t("ribbon.quickbar.current_layer" as never)}
              </button>
              <select
                className="context-select"
                data-testid="quickbar-layer-select"
                value={
                  state.propertyWorkflows.defaultObjectProperties.Layer ?? "0"
                }
                onChange={(event) => {
                  setCurrentLayer(event.target.value);
                }}
              >
                {(geometryDocument.styleTables?.layers ?? []).map((layer) => (
                  <option key={layer.name} value={layer.name}>
                    {layer.name}
                  </option>
                ))}
              </select>
            </span>
            <span
              className="ribbon-context-area"
              data-testid="quickbar-dimstyle"
            >
              {dimensionStyles.length > 0 ? (
                <button
                  className="context-link-button"
                  type="button"
                  onClick={withPanSupersede(dimStyleEditorDialog.open)}
                >
                  {t("ribbon.quickbar.current_dimstyle" as never)}
                </button>
              ) : (
                <span className="context-label">
                  {t("ribbon.quickbar.current_dimstyle" as never)}
                </span>
              )}
              <select
                className="context-select"
                data-testid="quickbar-dimstyle-select"
                // @codex P2 — reflect the CURRENT DIMSTYLE (the AutoCAD DIMSTYLE
                // sysvar, persisted as `defaultObjectProperties["Dimension style"]`
                // by Set Current), NOT `dimensionStyles[0]`: after "Set Current
                // Arch" new dimensions inherit Arch, so the always-visible quickbar
                // must show Arch too (mirrors the current-Layer quickbar above).
                // Falls back to the first table entry when the pointer is unset.
                value={
                  state.propertyWorkflows.defaultObjectProperties[
                    "Dimension style"
                  ] ??
                  dimensionStyles[0]?.name ??
                  ""
                }
                disabled
                title="Active dimension style (read-only)"
              >
                {dimensionStyles.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </span>
            <span
              className="ribbon-context-area"
              data-testid="quickbar-mleaderstyle"
            >
              {multileaderStyles.length > 0 ? (
                <button
                  className="context-link-button"
                  type="button"
                  onClick={withPanSupersede(mleaderStyleEditorDialog.open)}
                >
                  {t("ribbon.quickbar.current_mleaderstyle" as never)}
                </button>
              ) : (
                <span className="context-label">
                  {t("ribbon.quickbar.current_mleaderstyle" as never)}
                </span>
              )}
              <select
                className="context-select"
                data-testid="quickbar-mleaderstyle-select"
                value={multileaderStyles[0]?.name ?? ""}
                disabled
                title="Active multileader style (read-only)"
              >
                {multileaderStyles.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </span>
            <span
              className="ribbon-context-area"
              data-testid="quickbar-textstyle"
            >
              {textStyles.length > 0 ? (
                <button
                  className="context-link-button"
                  type="button"
                  onClick={withPanSupersede(textStyleEditorDialog.open)}
                >
                  {t("ribbon.quickbar.current_textstyle" as never)}
                </button>
              ) : (
                <span className="context-label">
                  {t("ribbon.quickbar.current_textstyle" as never)}
                </span>
              )}
              <select
                className="context-select"
                data-testid="quickbar-textstyle-select"
                value={textStyles[0]?.name ?? ""}
                disabled
                title="Active text style (read-only)"
              >
                {textStyles.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </span>
            {contextEntityType === "Text" || contextEntityType === "MText" ? (
              <span className="ribbon-context-area">
                <label className="context-label">{t("context.style")}</label>
                <select
                  className="context-select"
                  value={propertyDraft["Text style"] ?? ""}
                  onChange={(event) => {
                    setPropertyDraft((d) => ({
                      ...d,
                      "Text style": event.target.value,
                    }));
                    dispatch({
                      type: "properties/apply",
                      source: "toolbar",
                      entityIds: state.selection.entityIds,
                      values: { "Text style": event.target.value },
                    });
                  }}
                >
                  {textStyles.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <label className="context-label">{t("context.height")}</label>
                <input
                  className="context-input"
                  type="number"
                  step="0.5"
                  value={propertyDraft["Height"] ?? ""}
                  onChange={(event) =>
                    setPropertyDraft((d) => ({
                      ...d,
                      Height: event.target.value,
                    }))
                  }
                />
                <button
                  className="ghost-button"
                  type="button"
                  style={{ minHeight: 24, padding: "0 8px", fontSize: "11px" }}
                  onClick={withPanSupersede(textStyleEditorDialog.open)}
                  data-testid="ribbon-manage-textstyles-button"
                >
                  {t("context.manage_styles")}
                </button>
              </span>
            ) : null}
            {contextEntityType === "Dimension" ? (
              <span className="ribbon-context-area">
                <label className="context-label">
                  {t("context.dim_style")}
                </label>
                <select
                  className="context-select"
                  value={propertyDraft["Dimension style"] ?? ""}
                  onChange={(event) => {
                    setPropertyDraft((d) => ({
                      ...d,
                      "Dimension style": event.target.value,
                    }));
                    dispatch({
                      type: "properties/apply",
                      source: "toolbar",
                      entityIds: state.selection.entityIds,
                      values: { "Dimension style": event.target.value },
                    });
                  }}
                >
                  {dimensionStyles.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <button
                  className="ghost-button"
                  type="button"
                  style={{ minHeight: 24, padding: "0 8px", fontSize: "11px" }}
                  onClick={withPanSupersede(dimStyleEditorDialog.open)}
                  data-testid="ribbon-manage-dimstyles-button"
                >
                  {t("context.manage_styles")}
                </button>
                <label className="context-label">
                  {t("context.text_height")}
                </label>
                <input
                  className="context-input"
                  type="number"
                  step="0.5"
                  value={propertyDraft["Measurement"] ?? ""}
                  onChange={(event) =>
                    setPropertyDraft((d) => ({
                      ...d,
                      Measurement: event.target.value,
                    }))
                  }
                />
                <label className="context-label">
                  {t("context.arrow_size")}
                </label>
                <input
                  className="context-input"
                  type="number"
                  step="0.5"
                  value={propertyDraft["Arrow size"] ?? ""}
                  onChange={(event) =>
                    setPropertyDraft((d) => ({
                      ...d,
                      "Arrow size": event.target.value,
                    }))
                  }
                />
              </span>
            ) : null}
            {contextEntityType === "Multileader" ? (
              <span className="ribbon-context-area">
                <label className="context-label">
                  {t("context.mleader_style")}
                </label>
                <select
                  className="context-select"
                  data-testid="context-mleader-style-select"
                  value={propertyDraft["Multileader style"] ?? ""}
                  onChange={(event) => {
                    setPropertyDraft((d) => ({
                      ...d,
                      "Multileader style": event.target.value,
                    }));
                    dispatch({
                      type: "properties/apply",
                      source: "toolbar",
                      entityIds: state.selection.entityIds,
                      values: { "Multileader style": event.target.value },
                    });
                  }}
                >
                  {multileaderStyles.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <label className="context-label">{t("context.content")}</label>
                <select
                  className="context-select"
                  value={propertyDraft["Attachment"] ?? ""}
                  onChange={(event) =>
                    setPropertyDraft((d) => ({
                      ...d,
                      Attachment: event.target.value,
                    }))
                  }
                >
                  <option value="1">{t("context.content_block")}</option>
                  <option value="2">{t("context.content_mtext")}</option>
                </select>
                <label className="context-label">
                  {t("context.arrow_size")}
                </label>
                <input
                  className="context-input"
                  type="number"
                  step="0.5"
                  value={propertyDraft["Arrow size"] ?? ""}
                  onChange={(event) =>
                    setPropertyDraft((d) => ({
                      ...d,
                      "Arrow size": event.target.value,
                    }))
                  }
                />
                {/* Phase 7 — extended Multileader context toolbar:
                    Text height, Dogleg length, Landing gap, Leader
                    weight dropdown, and Annotative Yes/No dropdown.
                    These all route through the same `properties/apply`
                    path which, thanks to the Phase 7.1 translator
                    extension, now reaches the geometry document. */}
                <label className="context-label">
                  {t("context.mleader_text_height")}
                </label>
                <input
                  className="context-input"
                  data-testid="context-mleader-text-height"
                  type="number"
                  step="0.5"
                  value={propertyDraft["Text height"] ?? ""}
                  onChange={(event) => {
                    setPropertyDraft((d) => ({
                      ...d,
                      "Text height": event.target.value,
                    }));
                  }}
                  onBlur={(event) => {
                    dispatch({
                      type: "properties/apply",
                      source: "toolbar",
                      entityIds: state.selection.entityIds,
                      values: { "Text height": event.target.value },
                    });
                  }}
                />
                <label className="context-label">
                  {t("context.mleader_dogleg_length")}
                </label>
                <input
                  className="context-input"
                  data-testid="context-mleader-dogleg-length"
                  type="number"
                  step="0.5"
                  value={propertyDraft["Dogleg length"] ?? ""}
                  onChange={(event) => {
                    setPropertyDraft((d) => ({
                      ...d,
                      "Dogleg length": event.target.value,
                    }));
                  }}
                  onBlur={(event) => {
                    dispatch({
                      type: "properties/apply",
                      source: "toolbar",
                      entityIds: state.selection.entityIds,
                      values: { "Dogleg length": event.target.value },
                    });
                  }}
                />
                <label className="context-label">
                  {t("context.mleader_landing_gap")}
                </label>
                <input
                  className="context-input"
                  data-testid="context-mleader-landing-gap"
                  type="number"
                  step="0.5"
                  value={propertyDraft["Landing gap"] ?? ""}
                  onChange={(event) => {
                    setPropertyDraft((d) => ({
                      ...d,
                      "Landing gap": event.target.value,
                    }));
                  }}
                  onBlur={(event) => {
                    dispatch({
                      type: "properties/apply",
                      source: "toolbar",
                      entityIds: state.selection.entityIds,
                      values: { "Landing gap": event.target.value },
                    });
                  }}
                />
                <label className="context-label">
                  {t("context.mleader_leader_weight")}
                </label>
                {(() => {
                  const rawValue = propertyDraft["Leader weight"] ?? "";
                  const normalized = normalizeLineweightValue(rawValue);
                  const isPreset =
                    LINEWEIGHT_PRESET_VALUES.includes(normalized);
                  const selectValue = isPreset ? normalized : rawValue;
                  return (
                    <select
                      className="context-select"
                      data-testid="context-mleader-leader-weight"
                      value={selectValue}
                      onChange={(event) => {
                        setPropertyDraft((d) => ({
                          ...d,
                          "Leader weight": event.target.value,
                        }));
                        dispatch({
                          type: "properties/apply",
                          source: "toolbar",
                          entityIds: state.selection.entityIds,
                          values: { "Leader weight": event.target.value },
                        });
                      }}
                    >
                      {LINEWEIGHT_PRESET_VALUES.map((lw) => (
                        <option key={lw} value={lw}>
                          {lw === "ByLayer"
                            ? t("lineweight.bylayer")
                            : lw === "ByBlock"
                              ? t("lineweight.byblock")
                              : lw === "Default"
                                ? t("lineweight.default")
                                : t("lineweight.mm", { value: lw })}
                        </option>
                      ))}
                      {!isPreset && rawValue ? (
                        <option key={rawValue} value={rawValue}>
                          {rawValue}
                        </option>
                      ) : null}
                    </select>
                  );
                })()}
                <label className="context-label">
                  {t("context.mleader_annotative")}
                </label>
                <select
                  className="context-select"
                  data-testid="context-mleader-annotative"
                  value={propertyDraft["Annotative"] ?? "No"}
                  onChange={(event) => {
                    setPropertyDraft((d) => ({
                      ...d,
                      Annotative: event.target.value,
                    }));
                    dispatch({
                      type: "properties/apply",
                      source: "toolbar",
                      entityIds: state.selection.entityIds,
                      values: { Annotative: event.target.value },
                    });
                  }}
                >
                  <option value="Yes">{t("annotative.yes")}</option>
                  <option value="No">{t("annotative.no")}</option>
                </select>
              </span>
            ) : null}
          </div>
        </div>

        <div className="ribbon-panels">
          {visibleRibbonTabIds.map((tabId) => (
            <section
              key={tabId}
              className={`ribbon-panel ${activeRibbonTab === tabId ? "is-active" : ""}`}
            >
              {ribbonSections[tabId].map((section) => (
                <div key={section.title} className="ribbon-section">
                  <div className="ribbon-section-title">{section.title}</div>
                  <div className="ribbon-mini-grid">
                    {section.actions.map((action) =>
                      // CECOLOR — custom React control slot. The Properties
                      // section carries a `cecolor-swatch` action that renders
                      // the current-colour swatch instead of a plain button.
                      action.id === "cecolor-swatch" ? (
                        <CeColorSwatch key={action.id} />
                      ) : (
                      <button
                        key={action.id}
                        className={`tool-btn${
                          action.variant === "danger" ? " is-danger" : ""
                        }${action.variant === "primary" ? " is-primary" : ""}`}
                        type="button"
                        data-testid={`ribbon-action-${action.id}`}
                        title={`${action.label}\n${action.meta}`}
                        onClick={() => {
                          // CC-1e — ribbon button click telemetry (§14 "clicks"
                          // axis), emitted alongside the action's own command.
                          useEditorStore.getState().recordUiClick({
                            target: "ribbon",
                            controlId: `ribbon-action-${action.id}`,
                          });
                          action.onClick();
                        }}
                      >
                        <span className="tool-icon-wrap" aria-hidden="true">
                          {ribbonIconMap[action.id]
                            ? ribbonIconMap[action.id]()
                            : action.glyph}
                        </span>
                      </button>
                      ),
                    )}
                  </div>
                </div>
              ))}
            </section>
          ))}
        </div>
      </section>

      <main
        className="workspace-grid"
        data-testid="workspace-grid"
        data-inspector-collapsed={isInspectorCollapsed ? "true" : "false"}
        data-drop-zone-active={dropZoneActive ? "true" : "false"}
        onDragOver={handleWorkspaceDragOver}
        onDragEnter={handleWorkspaceDragEnter}
        onDragLeave={handleWorkspaceDragLeave}
        onDrop={handleWorkspaceDrop}
      >
        <section className="main-column" data-testid="main-column">
          <div
            className="drawing-area-flex"
            data-testid="drawing-area-flex"
            // cmd-pan — hand cursor while an interactive realtime-pan session is
            // active. Minimal CSS toggle (no cursor subsystem): the stylesheet
            // sets `cursor: grab` on the canvas under this data attribute.
            data-pan-session={panSessionActive ? "true" : "false"}
          >
            <RefeditToolbar />
            {/* N4 GRIPS-ARMED-STRETCH-I18N — RU/EN echo while a grip is click-armed. */}
            <GripArmedPrompt />
            {/* cmd-pan — net-new prompt strip surfaced ONLY during an
                interactive realtime-pan session. The e2e queries this node by
                `data-testid="command-pan-prompt"` (the zoom prompt has no testid
                and rides `statusText`; this pan prompt is a real DOM node). */}
            {panSessionActive ? (
              <div
                className="command-pan-prompt"
                data-testid="command-pan-prompt"
                role="status"
                aria-live="polite"
              >
                {t("pan.realtime_prompt")}
              </div>
            ) : null}
            {/* GRIPS-INTERACTION PR-2 fix — net-new status strip for the armed
                hot-grip modal prompt ("** STRETCH **" / "** MOVE **" / …),
                mirroring `command-pan-prompt` above (same directly-rendered,
                local-state-driven pattern — `commandFeed` has no consumer).
                fix/grip-armed-prompt-dedup — gated off while a grip is armed
                (`isGripArmedForEchoGate`) so this never stacks on top of
                `GripArmedPrompt` above, which already covers the armed case
                with strictly richer content (mode banner + point prompt vs.
                this strip's mode-banner-only text). This strip still renders
                its OTHER two states unaffected: the post-commit summary
                (`"summary.<mode>"`, set once `GripArmedPrompt` has already
                unmounted because the grip disarmed) and the empty-string
                abort clear (renders nothing either way). */}
            {gripModeEchoText !== "" && !isGripArmedForEchoGate ? (
              <div
                className="command-grip-mode-echo"
                data-testid="grip-mode-echo"
                role="status"
                aria-live="polite"
              >
                {gripModeEchoText}
              </div>
            ) : null}
            {/* PR-V3 — drop the >50k hard-guard. Scene3DStage's
                cache-direct path (PR-V2) reads from `__entitiesCache`
                + `entityMetadataIndex` for huge drawings, so the
                placeholder div is no longer needed. The renderer
                handles 685k+ entities natively via BatchedMesh. */}
            <Scene3DStage
              activeSpace={activeSpace}
              entities={activeEntities}
              modelSpaceEntities={modelSpaceEntities}
              blockDefinitions={geometryDocument.blocks ?? {}}
              linetypes={geometryDocument.linetypes ?? {}}
              // PR-LT3 variant A (A2) — GLOBAL dash-scale sysvars (LTSCALE ×
              // MS|PS). CELTSCALE (per-entity, group 48) is NOT here — it rides
              // the preview property bag → per-instance `instanceLtScale`. Same
              // document source as `linetypes`.
              linetypeSysvars={linetypeSysvars}
              textStyles={textStyles}
              selectedEntityIds={state.selection.entityIds}
              draftSession={draftSession}
              draftPreviewEntity={draftPreviewEntity}
              resetToken={stageResetToken}
              statusText={zoomPickingPrompt ?? stageStatusText}
              liveTextInput={liveTextInputProp}
              inPlaceEdit={inPlaceEditProp}
              onSelectEntity={selectEntityFromStage}
              onSelectEntities={selectEntitiesFromStage}
              onCanvasPoint={handleStagePoint}
              onCanvasHover={handleStageHover}
              onCursorClientChange={handleStageCursorClient}
              onGripDrag={handleGripDrag}
              // GRIPS-SUBSYSTEM (§5.11) — coalesce one grip drag into one
              // undo step. The drag fires `properties/apply` on every
              // pointermove; wrap the whole drag in the repo's grouped-undo
              // batch so a single Ctrl+Z reverts the entire grip move to its
              // pre-drag geometry (AutoCAD parity). Escape aborts the batch.
              // Handlers are stable `useCallback`s (Codex P1 #317) so a
              // mid-drag re-render does not churn their identity.
              onGripDragStart={handleGripDragStart}
              onGripDragEnd={handleGripDragEnd}
              onGripDragCancel={handleGripDragCancel}
              // GRIPS-INTERACTION PR-2 (Ctrl→Enter/Space correction, §8.2) —
              // the armed hot-grip modal's MOVE/ROTATE/SCALE/MIRROR commit
              // (cycled via Enter/Space) + its command-line echo. The SAME
              // `onGripDragStart`/`onGripDragEnd`/`onGripDragCancel` batch
              // above still brackets the whole gesture regardless of which
              // mode it ends in — `handleGripTransformCommit` only dispatches
              // the geometry command INTO that already-open batch.
              onGripTransformCommit={handleGripTransformCommit}
              onGripModeEcho={handleGripModeEcho}
              onEntityDoubleClick={handleEntityDoubleClick}
              stageCameraApiRef={stageCameraApiRef}
              onZoomPromptChange={setZoomPickingPrompt}
              // #284-6 — a completed INTERACTIVE zoom pick (two-corner ZOOM
              // Window / ZOOM Object entity pick) moves the camera later, in the
              // adapter's pick-completion callback — long after the command-line
              // ZW/ZO returned. Emit the view-op here so a completed interactive
              // zoom records exactly one zoom view-op (the synchronous
              // ZE/ZA/ZP/ZW-exact forms already record at dispatch above).
              onZoomViewOpComplete={(mode) => {
                useEditorStore.getState().recordViewOp({
                  opType: "zoom",
                  summary: t("audit.viewop_zoom", { mode }),
                  source: "scene",
                  detail: { mode },
                });
              }}
              // CC-1c — a real interactive PAN (MMB drag / two-finger touch)
              // completed and moved the paper camera. The typed PAN command
              // (PAN branch above) does NOT move the camera, so the ONLY honest
              // pan view-op is emitted here, ONCE at gesture end (never per
              // frame), carrying the cumulative world displacement. Geometry is
              // untouched — observability only (ТЗ §10.1 / §14).
              onPanComplete={({ deltaX, deltaY }) => {
                useEditorStore.getState().recordViewOp({
                  opType: "pan",
                  summary: t("audit.viewop_pan"),
                  source: "scene",
                  detail: { deltaX, deltaY },
                });
              }}
              // cmd-pan — interactive realtime-pan session (bare PAN / right-click
              // 'Pan'). The ref is read at LMB-drag time by the stage; Esc/Enter
              // on the canvas fires onPanSessionEnd to clear the session.
              panSessionActiveRef={panSessionActiveRef}
              onPanSessionEnd={endPanSession}
              coordinateSystem={coordinateSystem}
              onRightClickEnter={() => submitCommandTextRef.current("")}
              onContextMenu={handleStageContextMenu}
              onStageText={(text) => submitCommandTextRef.current(text)}
              onOpenDynInputSettings={withPanSupersede(() =>
                openDsettingsOnTab("dynamic-input"),
              )}
              cannoscaleName={geometryDocument.cannoscaleName}
              annoAllVisible={geometryDocument.annoAllVisible}
              fillmode={geometryDocument.fillmode}
              annoAutoScale={geometryDocument.annoAutoScale}
              annoMonitor={geometryDocument.annoMonitor}
              annotationScales={geometryDocument.annotationScales}
              onOpenScaleListEditDialog={withPanSupersede(
                scaleListEditDialog.open,
              )}
              blockRecords={geometryDocument.blockRecords ?? {}}
              printPreviewWorldBounds={printDialog.printPreviewWorldBounds}
              windowPickActive={printDialog.windowPick.isPicking}
              onWindowPicked={printDialog.windowPick.onPicked}
              onWindowPickCancel={printDialog.windowPick.onCancel}
              onBlockLookupGripClick={({ entityId, parameterId, screen }) =>
                setBlockLookupMenu({ entityId, parameterId, screen })
              }
              onMleaderGripContextMenu={({
                entityId,
                gripKind,
                gripPayload,
                gripWorld,
                screen,
              }) => {
                // Filter to mleader-* kinds: the gripsOverlay already
                // filters but reaffirm at the host so a future
                // refactor can't accidentally flow non-mleader grips
                // here.
                if (
                  gripKind !== "mleader-arrow" &&
                  gripKind !== "mleader-vertex" &&
                  gripKind !== "mleader-landing" &&
                  gripKind !== "mleader-dogleg" &&
                  gripKind !== "mleader-text"
                )
                  return;
                setMleaderGripMenu({
                  entityId,
                  gripKind,
                  gripPayload,
                  gripWorld,
                  screen,
                });
              }}
              // B8 — Dynamic Input overlay. Handed to Scene3DStage as its
              // stage-shell slot instead of being rendered here, so it mounts
              // INSIDE the stage root div (still OUTSIDE `<Canvas>`, so the
              // R3F reconciler never sees the DOM children). It reads cursor
              // world coords + camera matrix from module-level snapshots
              // (cursorWorldSnapshot, cameraSnapshot) and places its children
              // at `projectWorldToClient()` pixels, which are CANVAS-relative
              // — so the canvas box has to be their containing block. As a
              // direct child of `.drawing-area-flex` (`display: flex`, no
              // `position`) they resolved against `.app-shell` instead and
              // drew ~420 px above the crosshair, on top of the ribbon.
              stageShellOverlay={
                <DynInputOverlay
                  draftSession={draftSession}
                  draftReferencePoint={draftReferencePointWithFilter(draftSession)}
                  activeSpaceName={activeSpace.name}
                  onCommit={(text) => submitCommandTextRef.current(text)}
                  onCancel={() => submitCommandTextRef.current("")}
                  onOpenSettings={withPanSupersede(() =>
                    openDsettingsOnTab("dynamic-input"),
                  )}
                />
              }
            />

            {stageContextMenu ? (
              <StageContextMenu
                screenPos={stageContextMenu.screenPos}
                mode={stageContextMenu.mode}
                title={t(
                  stageContextMenu.mode === "edit"
                    ? "context_menu.title.edit"
                    : stageContextMenu.mode === "command"
                      ? "context_menu.title.command"
                      : stageContextMenu.mode === "osnap"
                        ? "context_menu.title.osnap"
                        : "context_menu.title.default",
                )}
                items={buildContextMenuItems(stageContextMenu.mode)}
                onClose={closeStageContextMenu}
              />
            ) : null}

            {/* AutoCAD ROLLOVERTIPS — property card on hover. The card
                hides itself when ROLLOVERTIPS=0 / no hover entity / no
                cursor anchor, so unconditional mount is safe. */}
            <ToolTipCard />

            {/* B12 — SELECTIONCYCLING badge + dialog overlays. Both are
                driven by the cycling store (populated by stageInteractions
                hover hit-test and the click handler), so unconditional
                mount is safe — they self-hide when no stack is active. */}
            <SelectionCyclingBadgeMount />
            <SelectionCyclingDialogMount />

            {/* B15 — DXF drag-and-drop drop-zone visual indicator. Pure
                presentational; the listeners live on the <main> element
                above. */}
            {dropZoneActive ? (
              <div
                data-testid="dxf-drop-zone-overlay"
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 50,
                  pointerEvents: "none",
                  background: "rgba(60, 130, 220, 0.15)",
                  border: "3px dashed rgba(60, 130, 220, 0.85)",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 8,
                  fontFamily: "system-ui, sans-serif",
                  color: "#1d3a66",
                  fontSize: 18,
                  fontWeight: 600,
                  textShadow: "0 1px 0 rgba(255,255,255,0.6)",
                }}
              >
                <div data-testid="dxf-drop-zone-label">
                  {t("dxfio.drop_zone_label")}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 400,
                    color: "#345279",
                  }}
                  data-testid="dxf-drop-zone-hint"
                >
                  {t("dxfio.drop_zone_hint")}
                </div>
              </div>
            ) : null}
            {dropZoneError ? (
              <div
                role="alert"
                data-testid="dxf-drop-zone-error"
                style={{
                  position: "absolute",
                  bottom: 12,
                  left: 12,
                  zIndex: 51,
                  padding: "6px 10px",
                  background: "rgba(160, 30, 30, 0.92)",
                  color: "#fff",
                  borderRadius: 4,
                  fontFamily: "system-ui, sans-serif",
                  fontSize: 12,
                }}
              >
                {dropZoneError}
              </div>
            ) : null}

            {/* Phase MC1 — SpaceTabsBar replaces the previous inline
                space-tabs-compact + inline rename logic. Drag-reorder,
                context menu stubs, and SpaceModeBadge live in the component. */}
            <SpaceTabsBar
              windowPickActive={printDialog.windowPick.isPicking}
              // PDF-6.9b — thread the committed PDF Options config into the
              // multi-sheet PUBLISH path so it honours vector DPI, omitted
              // layers, and the bookmarks toggle.
              getPdfOptionsConfig={pdfOptionsDialog.getConfig}
            />
          </div>
          {/* end drawing-area-flex */}

          <div
            className="command-inline-wrap"
            data-testid="command-form-inline"
          >
            {commandHistoryOpen ? (
              <div
                className="command-dropdown-up"
                data-testid="command-history-up"
              >
                <div className="command-history-list">
                  {state.commandLine.transcript.length === 0 ? (
                    <div className="transcript-line info">
                      {t("shell.command_stream_placeholder")}
                    </div>
                  ) : (
                    state.commandLine.transcript.map((entry) => (
                      <div
                        key={entry.id}
                        className={`transcript-line ${entry.tone}`}
                      >
                        {entry.message}
                      </div>
                    ))
                  )}
                </div>
                {commandSuggestions.length > 0 ? (
                  <div className="command-suggestions-popup">
                    {commandSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.id}
                        className={`command-suggestion command-suggestion-${suggestion.kind}`}
                        type="button"
                        onClick={() => {
                          setCommandText(suggestion.insertText);
                          if (suggestion.submit) {
                            submitCommandTextRef.current(suggestion.insertText);
                          }
                        }}
                      >
                        <span>{suggestion.label}</span>
                        <code>{suggestion.insertText}</code>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
            <form
              className="command-form console-entry command-form-inline"
              onSubmit={submitCommand}
            >
              <label className="entry-label" htmlFor="command-input-inline">
                {t("shell.command_label")}
              </label>
              <input
                id="command-input-inline"
                className="command-input"
                data-testid="command-input-inline"
                value={commandText}
                onChange={(event) => setCommandText(event.target.value)}
                onFocus={(event) => event.currentTarget.select()}
                // A03 (R17 seam) — this input's own local Enter/Escape
                // handling (distinct from the GLOBAL window listener in
                // useKeyboardShortcuts.ts) moved verbatim to
                // `hooks/useCommandLine.ts`'s
                // `createCommandLineInputKeyDownHandler` (same
                // rebuilt-every-render factory lifetime as the inline arrow
                // function it replaces — see that function's own comment for
                // why each branch exists).
                onKeyDown={createCommandLineInputKeyDownHandler({
                  panSessionActiveRef,
                  commandText,
                  endPanSession,
                  submitCommandText,
                  draftSession,
                  setDraftSession,
                  setCommandText,
                  setCommandHistoryOpen,
                  clearColorTransientState,
                })}
                placeholder={t("shell.command_input_placeholder")}
              />
              <button className="primary-button console-btn" type="submit">
                {t("shell.run_submit")}
              </button>
              <button
                className="ghost-button console-btn console-btn-muted"
                type="button"
                onClick={() => {
                  setDraftSession(null);
                  setCommandText("");
                  setCommandHistoryOpen(false);
                  // CECOLOR round-9 ROOT CURE — the inline "Esc" button is a
                  // MOUSE click, so it can never be covered by any keyboard
                  // Escape handler: it must tear down the colour-input context
                  // itself. Route through the shared `clearColorTransientState()`
                  // (closes the command-line COLOR picker, clears the `-COLOR`
                  // pending-reply flag, bumps the swatch reset epoch) — the same
                  // teardown as the global Escape and every other seam.
                  clearColorTransientState();
                }}
              >
                {t("shell.esc_button")}
              </button>
              <button
                className="ghost-button console-btn console-btn-arrow"
                type="button"
                aria-expanded={commandHistoryOpen}
                title={t("shell.command_history_title")}
                onClick={() => setCommandHistoryOpen((current) => !current)}
                data-testid="command-history-toggle-inline"
              >
                <span aria-hidden="true">{commandHistoryOpen ? "▾" : "▴"}</span>
              </button>
            </form>
          </div>

          <SessionShellCard
            onReset={resetShellState}
            onOpenFontManager={withPanSupersede(fontManagerDialog.open)}
          />

          <article className="surface-card" data-testid="space-routing-card">
            <div className="section-heading">
              <div>
                <p className="section-label">{t("spaces.section_label")}</p>
                <h2>{t("spaces.title")}</h2>
              </div>
              <span className="badge">{activeSpace.name}</span>
            </div>

            <div className="space-tabs" data-testid="space-tabs">
              {state.drawing.spaces.map((space) =>
                renamingSpaceId === space.id ? (
                  // B2 — inline rename editor on the Spaces routing card.
                  // Mirrors the compact space-tabs-inline section above.
                  <input
                    key={space.id}
                    className={`tab-button is-renaming ${
                      space.id === activeSpace.id ? "is-active" : ""
                    }`}
                    data-testid={`space-tab-routing-rename-input-${toTestIdSegment(space.id)}`}
                    type="text"
                    autoFocus
                    value={renamingDraftName}
                    placeholder={t("layout.rename.placeholder")}
                    maxLength={255}
                    onChange={(event) =>
                      setRenamingDraftName(event.target.value)
                    }
                    onBlur={commitRenameSpace}
                    onFocus={(event) => event.currentTarget.select()}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        commitRenameSpace();
                      } else if (event.key === "Escape") {
                        event.preventDefault();
                        cancelRenameSpace();
                      }
                    }}
                  />
                ) : (
                  <button
                    key={space.id}
                    className={`tab-button ${space.id === activeSpace.id ? "is-active" : ""}`}
                    type="button"
                    data-testid={`space-tab-${toTestIdSegment(space.id)}`}
                    onClick={() => {
                      // CC-1e — layout/space tab CLICK navigation telemetry
                      // (§14): the click is the "clicks" axis, distinct from
                      // the MSPACE/PSPACE command. Emitted alongside the
                      // unchanged activate-space command dispatch.
                      useEditorStore.getState().recordUiNav({
                        navKind: "space",
                        from: state.drawing.activeSpaceId,
                        to: space.id,
                      });
                      dispatch({
                        type: "shell/activate-space",
                        source: "toolbar",
                        spaceId: space.id,
                        layoutContext:
                          space.kind === "layout" ? "paper" : "model",
                      });
                    }}
                    onDoubleClick={() => beginRenameSpace(space.id)}
                  >
                    {space.name}
                  </button>
                ),
              )}
            </div>

            {isLayoutSpace ? (
              <div
                className="context-toggle"
                data-testid="layout-context-toggle"
              >
                <button
                  className={`tab-button ${state.drawing.layoutContext === "paper" ? "is-active" : ""}`}
                  type="button"
                  data-testid="layout-context-paper-button"
                  onClick={() => {
                    // CC-1e P2-3 — emit the declared `layout-context` ui-nav
                    // (§14 "clicks"): the paper↔viewport switch is a non-command
                    // navigation, distinct from the MSPACE/PSPACE command.
                    // Additive — emitted alongside the unchanged dispatch,
                    // mirroring the space-tab recordUiNav site above.
                    useEditorStore.getState().recordUiNav({
                      navKind: "layout-context",
                      from: state.drawing.layoutContext,
                      to: "paper",
                    });
                    dispatch({
                      type: "shell/set-layout-context",
                      source: "toolbar",
                      layoutContext: "paper",
                    });
                  }}
                >
                  {t("spaces.paper_space")}
                </button>
                <button
                  className={`tab-button ${state.drawing.layoutContext === "viewport" ? "is-active" : ""}`}
                  type="button"
                  data-testid="layout-context-viewport-button"
                  onClick={() => {
                    // CC-1e P2-3 — emit the declared `layout-context` ui-nav
                    // (§14 "clicks"): the paper↔viewport switch is a non-command
                    // navigation, distinct from the MSPACE/PSPACE command.
                    // Additive — emitted alongside the unchanged dispatch,
                    // mirroring the space-tab recordUiNav site above.
                    useEditorStore.getState().recordUiNav({
                      navKind: "layout-context",
                      from: state.drawing.layoutContext,
                      to: "viewport",
                    });
                    dispatch({
                      type: "shell/set-layout-context",
                      source: "toolbar",
                      layoutContext: "viewport",
                    });
                  }}
                >
                  {t("spaces.model_in_viewport")}
                </button>
              </div>
            ) : null}

            <div className="space-stage" data-testid="space-stage">
              <div
                className="space-summary-card"
                data-testid="space-summary-card"
              >
                <span className="section-label">{t("spaces.setup")}</span>
                <strong>{activeSpace.pageSetup}</strong>
                <p>
                  {isLayoutSpace
                    ? `${state.drawing.layoutContext}`
                    : t("spaces.setup_hint")}
                </p>
              </div>

              <div className="entity-column" data-testid="entity-column">
                <div className="section-heading">
                  <div>
                    <p className="section-label">
                      {t("spaces.entities_in_space")}
                    </p>
                    <h3>
                      {t("spaces.objects_in_drawing", {
                        count: activeEntitiesCount,
                      })}
                    </h3>
                  </div>
                  {selectedEntities.length > 0 ? (
                    <button
                      className="ghost-button"
                      type="button"
                      onClick={() =>
                        dispatch({
                          type: "shell/clear-selection",
                          source: "scene",
                        })
                      }
                    >
                      {t("spaces.clear_selection")}
                    </button>
                  ) : null}
                </div>

                <div className="entity-list" data-testid="entity-list">
                  {/*
                    PR-V4 — entity list virtualization. The list reads
                    from the metadata index so it covers every entity
                    in the active space (incl. cache-stash huge-import
                    rows). Up to ENTITY_LIST_VIEW_CAP rows render at
                    once; the "show all" button below toggles past the
                    cap. Each row falls back to the matching
                    EntityPreview from `activeEntities` for label /
                    summary when present (small imports), otherwise
                    derives a synthetic header from the metadata
                    record's type + layer.
                  */}
                  {(() => {
                    const previewById = new Map(
                      activeEntities.map((entity) => [entity.id, entity]),
                    );
                    const total = activeSpaceMetadata.length;
                    const visible =
                      entityListShowAll || total <= ENTITY_LIST_VIEW_CAP
                        ? activeSpaceMetadata
                        : activeSpaceMetadata.slice(0, ENTITY_LIST_VIEW_CAP);
                    const hidden = total - visible.length;
                    return (
                      <>
                        {visible.map((record) => {
                          const isSelected = state.selection.entityIds.includes(
                            record.id,
                          );
                          const isMatchpropSource =
                            matchpropSourceEntity?.id === record.id;
                          const preview = previewById.get(record.id);
                          const label = preview?.label ?? record.id;
                          const summary =
                            preview?.summary ??
                            t("entity_list.summary_fallback", {
                              layer: record.layer,
                            });

                          return (
                            <button
                              key={record.id}
                              className={`entity-card ${isSelected ? "is-selected" : ""}`}
                              type="button"
                              data-testid={`entity-card-${toTestIdSegment(record.id)}`}
                              onClick={(event) => {
                                // AutoCAD parity: plain click replaces
                                // the selection with this entity.
                                // Shift-click extends the current
                                // selection (or removes the clicked
                                // entity if it was already selected).
                                if (event.shiftKey) {
                                  const current = state.selection.entityIds;
                                  const nextIds = current.includes(record.id)
                                    ? current.filter((id) => id !== record.id)
                                    : [...current, record.id];
                                  if (nextIds.length === 0) {
                                    dispatch({
                                      type: "shell/clear-selection",
                                      source: "scene",
                                    });
                                  } else {
                                    dispatch({
                                      type: "shell/select-entities",
                                      source: "scene",
                                      entityIds: nextIds,
                                    });
                                  }
                                  return;
                                }
                                dispatch({
                                  type: "shell/select-entities",
                                  source: "scene",
                                  entityIds: [record.id],
                                });
                              }}
                            >
                              <span className="entity-type">{record.type}</span>
                              <strong>{label}</strong>
                              <span className="entity-summary">{summary}</span>
                              {isMatchpropSource ? (
                                <span className="mini-pill">
                                  {t("matchprop.source_pill")}
                                </span>
                              ) : null}
                            </button>
                          );
                        })}
                        {hidden > 0 ? (
                          <button
                            className="ghost-button"
                            type="button"
                            data-testid="entity-list-show-all-button"
                            onClick={() => setEntityListShowAll(true)}
                            style={{ margin: "8px 0" }}
                          >
                            {t("entity_list.show_all", { count: hidden })}
                          </button>
                        ) : null}
                        {entityListShowAll && total > ENTITY_LIST_VIEW_CAP ? (
                          <button
                            className="ghost-button"
                            type="button"
                            data-testid="entity-list-collapse-button"
                            onClick={() => setEntityListShowAll(false)}
                            style={{ margin: "8px 0" }}
                          >
                            {t("entity_list.collapse")}
                          </button>
                        ) : null}
                      </>
                    );
                  })()}
                </div>
              </div>

              {isLayoutSpace ? (
                <div className="viewport-grid" data-testid="viewport-grid">
                  {activeSpace.viewports.map((viewport) => (
                    <article
                      key={viewport.id}
                      className="viewport-card"
                      data-testid={`viewport-card-${toTestIdSegment(viewport.id)}`}
                    >
                      <span className="section-label">
                        {t("spaces.viewport")}
                      </span>
                      <strong>{viewport.name}</strong>
                      <span>
                        {viewport.scale} / {viewport.status}
                      </span>
                      <span>
                        {viewport.clipped
                          ? t("spaces.clipped_boundary")
                          : t("spaces.rect_boundary")}{" "}
                        /{" "}
                        {t("spaces.layer_overrides", {
                          count: viewport.overrideLayerCount,
                        })}
                      </span>
                    </article>
                  ))}
                </div>
              ) : null}
            </div>
          </article>

          <article className="surface-card" data-testid="dxf-io-card">
            <div className="section-heading">
              <div>
                <p className="section-label">{t("dxfio.title")}</p>
                <h2>{t("dxfio.title")}</h2>
              </div>
              <span className="badge">{t("dxfio.summary")}</span>
            </div>

            <div className="panel-stack">
              <div className="section-subcard" data-testid="dxf-import-form">
                <div className="section-heading">
                  <div>
                    <p className="section-label">{t("dxfio.import")}</p>
                    <h3>{t("dxfio.import")}</h3>
                  </div>
                  <span className="badge">{t("dxfio.import_picker")}</span>
                </div>
                <input
                  ref={dxfImportInputRef}
                  type="file"
                  accept=".dxf,text/plain"
                  className="sr-only"
                  data-testid="dxf-import-input"
                  onChange={handleDxfFileSelected}
                />
                <div className="action-row">
                  <button
                    className="primary-button"
                    type="button"
                    data-testid="dxf-import-button"
                    onClick={() => dxfImportInputRef.current?.click()}
                  >
                    {t("dxfio.import_button")}
                  </button>
                  <span className="microcopy">
                    {t("dxfio.import_microcopy")}
                  </span>
                </div>
              </div>

              <div className="section-subcard" data-testid="dxf-export-panel">
                <div className="section-heading">
                  <div>
                    <p className="section-label">{t("dxfio.export")}</p>
                    <h3>{t("dxfio.export")}</h3>
                  </div>
                  <span className="badge" data-testid="dxf-export-count">
                    {t("dxfio.export_entities_count", {
                      count: geometryDocument.spaces.reduce(
                        (count, space) => count + space.entities.length,
                        0,
                      ),
                    })}
                  </span>
                </div>
                <textarea
                  className="dxf-textarea"
                  data-testid="dxf-export-textarea"
                  value={cappedDxfPreviewText}
                  readOnly
                  spellCheck={false}
                  rows={14}
                />
                <div className="dxf-export-actions">
                  <button
                    className="ghost-button"
                    data-testid="dxf-download-btn"
                    type="button"
                    onClick={() => {
                      // Codex round 3 #6 — build the DXF on click only.
                      // For huge drawings the preview textarea is stale
                      // (gated by DXF_PREVIEW_AUTO_REFRESH_LIMIT) but
                      // the download always reflects the current state.
                      const dxfText = buildExportedDxfText({
                        emitAuditWarning: true,
                      });
                      const blob = new Blob([dxfText], {
                        type: "application/dxf;charset=utf-8",
                      });
                      const url = URL.createObjectURL(blob);
                      const anchor = document.createElement("a");
                      anchor.href = url;
                      anchor.download = `${(geometryDocument.title || "drawing").replace(/[^a-zA-Z0-9_-]/gu, "_")}.dxf`;
                      anchor.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    {t("dxfio.download")}
                  </button>
                  <button
                    className="ghost-button"
                    data-testid="dxf-copy-btn"
                    type="button"
                    onClick={() => {
                      // Codex round 3 #6 — build the DXF on click only.
                      void navigator.clipboard.writeText(
                        buildExportedDxfText({ emitAuditWarning: true }),
                      );
                    }}
                  >
                    {t("dxfio.copy_clipboard")}
                  </button>
                </div>
                <p className="microcopy" data-testid="dxf-import-status">
                  {dxfImportStatus}
                </p>
              </div>
            </div>
          </article>
        </section>

        <InspectorSidebar
          isCollapsed={isInspectorCollapsed}
          onToggleCollapse={toggleInspectorCollapsed}
          propertiesView={propertiesView}
          propertyDraft={propertyDraft}
          setPropertyDraft={setPropertyDraft}
          contextEntityType={contextEntityType}
          getStyleOptions={getStyleOptions}
          onSubmitProperties={submitProperties}
          onAutoApply={autoApplyProperties}
          onOpenDimStyleEditor={withPanSupersede(dimStyleEditorDialog.open)}
          onOpenMleaderStyleEditor={withPanSupersede(
            mleaderStyleEditorDialog.open,
          )}
          onOpenTextStyleEditor={withPanSupersede(textStyleEditorDialog.open)}
          onOpenLayerManager={withPanSupersede(() =>
            setLayerManagerOpen(true),
          )}
          onOpenPanel={openPanel}
          getDynamicPropertyDescriptor={getDynamicPropertyDescriptor}
          viewportInspectorPanel={
            selectedViewportEntity ? (
              <ViewportInspector
                viewport={selectedViewportEntity}
                allLayerNames={allLayerNamesForVP}
              />
            ) : undefined
          }
        />
      </main>

      <StatusStrips
        backendRuntime={backendRuntime}
        backendPersistentSlice={backendPersistentSlice}
      />

      <AppStyleEditorDialogs
        dimStyleEditor={dimStyleEditorDialog}
        mleaderStyleEditor={mleaderStyleEditorDialog}
        textStyleEditor={textStyleEditorDialog}
        fontManager={fontManagerDialog}
        DimStyleEditorDialog={DimStyleEditorDialog}
        MultileaderStyleEditorDialog={MultileaderStyleEditorDialog}
        TextStyleEditorDialog={TextStyleEditorDialog}
        FontManagerDialog={FontManagerDialog}
      />

      {/* CC-3 special-editor host (REVCLOUD-PROPS-EDITOR §5.1). Renders the
          registered per-entity form for the open special-editor target
          (REVCLOUDPROPERTIES opens a Revision Cloud here). */}
      <AppSpecialEditorDialogs editor={specialEditor} />

      {layerManagerOpen ? (
        <Suspense fallback={null}>
          <LayerManagerDialog
            layers={geometryDocument.styleTables?.layers ?? []}
            currentLayerName={
              state.propertyWorkflows.defaultObjectProperties.Layer ?? "0"
            }
            entityCountsByLayer={entityCountsByLayer}
            onUpdateLayer={updateLayer}
            onAddLayer={addLayer}
            onDeleteLayer={deleteLayer}
            onRenameLayer={renameLayer}
            onSetCurrentLayer={setCurrentLayer}
            onClose={() => setLayerManagerOpen(false)}
          />
        </Suspense>
      ) : null}

      {/* Drafting Settings dialog (DSETTINGS / DDRMODES / right-click DIN) */}
      <DSettingsDialog
        open={dsettingsOpen}
        onOpenChange={setDsettingsOpen}
        initialTab={dsettingsInitialTab}
      />

      {/* CECOLOR — "Select Color" dialog opened by COLOR/SETCOLOR/ЦВЕТ (no value).
          Routes the selection through the invertible `header/set-cecolor` command;
          `editorStore.dispatch` mirrors `doc.cecolor` into the live current-colour
          store (one truth — same path as the ribbon swatch, CLI, and AI). */}
      {ceColorPickerOpen ? (
        <AciColorPicker
          currentAci={liveCeColor.kind === "index" ? liveCeColor.aci : 256}
          initialTab={liveCeColor.kind === "rgb" ? "true" : "index"}
          initialHex={
            liveCeColor.kind === "rgb"
              ? `#${[liveCeColor.r, liveCeColor.g, liveCeColor.b]
                  .map((n) => (n & 0xff).toString(16).padStart(2, "0"))
                  .join("")}`
              : null
          }
          // CECOLOR round-8 FINDING C — the COLOR/SETCOLOR command picker is a
          // CECOLOR entry point, so it must expose the FULL EntityColor union
          // (ByLayer / ByBlock), exactly like the ribbon swatch (round-7 D).
          // Without these props a user opening the dialog from the command line
          // could pick ACI / true-colour but NOT return to ByLayer / ByBlock.
          showByReference
          currentReference={
            liveCeColor.kind === "byLayer"
              ? "byLayer"
              : liveCeColor.kind === "byBlock"
                ? "byBlock"
                : undefined
          }
          anchorX={120}
          anchorY={120}
          onSelect={(value) => {
            const color = coerceColor(value);
            dispatch({
              type: "header/set-cecolor",
              source: "toolbar",
              color,
            });
            // round-9 ROOT CURE — selecting a colour from the COLOR/SETCOLOR
            // command picker ENDS the colour-input context, so tear it all down
            // via the shared clearer (closes this picker, clears any still-armed
            // `-COLOR` pending-reply flag — same class as FINDING A — and bumps
            // the swatch reset epoch). Without clearing the pending flag a
            // `-COLOR` armed alongside an opened picker would swallow the next
            // command-line submission.
            clearColorTransientState();
          }}
          onClose={() => setCeColorPickerOpen(false)}
        />
      ) : null}

      {/* AutoCAD-style "Processing Objects" modal — auto-mounts via its
          internal selector when state.importProgress.active === true. */}
      <ImportProgressDialog />

      {/* Phase 2.2 slice 3: all remaining dialog mounts live in
          `AppRemainingDialogs` so this section is a single component
          mount. Grip context menus (BlockLookupMenu, MleaderGripMenu)
          stay below — they receive heavy callback prop bundles that
          warrant their own follow-up extraction. */}

      <AppGripMenus
        blockLookupMenu={blockLookupMenu}
        setBlockLookupMenu={setBlockLookupMenu}
        mleaderGripMenu={mleaderGripMenu}
        setMleaderGripMenu={setMleaderGripMenu}
        dispatch={dispatch}
        geometryDocument={geometryDocument}
        appendCommandFeed={appendCommandFeed}
        t={t}
        BlockLookupMenu={BlockLookupMenu}
        MleaderGripContextMenu={MleaderGripContextMenu}
      />

      <AppRemainingDialogs
        blockDefinitionDialog={blockDefinitionDialog}
        insertBlockDialog={insertBlockDialog}
        overlapWarningDialog={overlapWarningDialog}
        printDialog={printDialog}
        blockEditExitDialog={blockEditExitDialog}
        scaleListEditDialog={scaleListEditDialog}
        annotativeObjectScaleDialog={annotativeObjectScaleDialog}
        xrefDialog={xrefDialog}
        writeBlockDialogOpen={writeBlockDialogOpen}
        writeBlockProps={{
          onConfirm: handleWriteBlockConfirm,
          onCancel: handleWriteBlockCancel,
        }}
        purgeBlocksDialogOpen={purgeBlocksDialogOpen}
        purgeBlocksProps={{
          onConfirm: handlePurgeBlocksConfirm,
          onCancel: handlePurgeBlocksCancel,
        }}
        purgeCandidates={purgeCandidates}
        renameBlockDialogOpen={renameBlockDialogOpen}
        renameBlockProps={{
          onConfirm: handleRenameBlockConfirm,
          onCancel: handleRenameBlockCancel,
        }}
        validateRenameForDialog={validateRenameForDialog}
        geometryDocument={geometryDocument}
        selectedEntities={selectedEntities}
        BlockDefinitionDialog={BlockDefinitionDialog}
        InsertBlockDialog={InsertBlockDialog}
        OverlapWarningDialog={OverlapWarningDialog}
        PrintPreviewDialog={PrintPreviewDialog}
        BlockEditExitDialog={BlockEditExitDialog}
        ScaleListEditDialog={ScaleListEditDialog}
        AnnotativeObjectScaleDialog={AnnotativeObjectScaleDialog}
        XrefManagerDialog={XrefManagerDialog}
        WriteBlockDialog={WriteBlockDialog}
        PurgeBlocksDialog={PurgeBlocksDialog}
        RenameBlockDialog={RenameBlockDialog}
      />

      {/* PDF-6.9a — Page Setup Manager dialog. Owned by
          `usePageSetupDialog`. Mounted inline (vs in AppRemainingDialogs)
          because the prop bundle carries a Map and several callbacks the
          slot's strict ComponentType prop wouldn't accept without a
          generic widening. The lazy chunk + Suspense fallback match the
          other dialog mounts in AppRemainingDialogs. */}
      {pageSetupDialog.isOpen ? (
        <Suspense fallback={null}>
          <PageSetupManagerDialog
            {...pageSetupDialog.props}
            onOpenPdfOptions={pdfOptionsDialog.open}
          />
        </Suspense>
      ) : null}

      {/* PDF-6.9b — PDF Options sub-dialog. Mounted alongside the Page
          Setup dialog; both can be in the DOM at once (the sub-dialog
          sits on top of the parent overlay). `usePdfOptionsDialog`'s
          `config` / `getConfig` exposes the committed values; the
          PRINT and PUBLISH paths read them at print-time. */}
      {pdfOptionsDialog.isOpen ? (
        <Suspense fallback={null}>
          <PdfOptionsDialog {...pdfOptionsDialog.props} />
        </Suspense>
      ) : null}

      <AppAttributeDialogs
        eattextDialog={eattextDialog}
        attributeDialogs={attributeDialogs}
        battmanDialog={battmanDialog}
        bpmDialog={bpmDialog}
        geometryDocument={geometryDocument}
        resolveSelectedInsertForAttributeEditing={
          resolveSelectedInsertForAttributeEditing
        }
        appendCommandFeed={appendCommandFeed}
        t={t}
        AttributeExtractionDialog={AttributeExtractionDialog}
        AttributeDefinitionDialog={AttributeDefinitionDialog}
        BlockAttributeManagerDialog={BlockAttributeManagerDialog}
        BlockPropertiesManagerDialog={BlockPropertiesManagerDialog}
        EditAttributesDialog={EditAttributesDialog}
        EnhancedAttributeEditorDialog={EnhancedAttributeEditorDialog}
      />

      <AppBlockAuthoringLayer
        geometryDocument={geometryDocument}
        state={state}
        activeSpace={activeSpace}
        blockAuthoringShell={blockAuthoringShell}
        dispatch={dispatch}
        appendCommandFeed={appendCommandFeed}
        t={t}
        startDraftSession={startDraftSession}
        setDraftSession={setDraftSession}
        expandParameterSet={expandParameterSet}
        BlockAuthoringPalette={BlockAuthoringPalette}
        BlockAuthoringPaletteShell={BlockAuthoringPaletteShell}
      />

      <AppBlockEditOverlays
        geometryDocument={geometryDocument}
        lookupTableDialog={lookupTableDialog}
        setLookupTableDialog={setLookupTableDialog}
        blockPropertiesDialogOpen={blockPropertiesDialogOpen}
        setBlockPropertiesDialogOpen={setBlockPropertiesDialogOpen}
        dispatch={dispatch}
        appendCommandFeed={appendCommandFeed}
        t={t}
        LookupTableDialog={LookupTableDialog}
        BlockPropertiesTableDialog={BlockPropertiesTableDialog}
      />
    </div>
  );
}

/**
 * B12 — cursor adornment that appears next to the pointer when the
 * cycling store reports ≥2 stacked candidates and SELECTIONCYCLING is
 * non-zero. The cursor position is read from the existing hover store
 * (already updated on every pointermove for ROLLOVERTIPS placement).
 *
 * Co-located in App.tsx (rather than a separate file) because the
 * mount only needs read-only zustand selectors and the logic is < 30
 * LOC — extracting it would force the callers to thread the cycling
 * store through props for no architectural benefit.
 */
function SelectionCyclingBadgeMount(): ReactElement | null {
  const cursorClient = stageHoverStore((s) => s.cursorClient);
  const stackedCandidates = useSelectionCyclingStore(
    (s) => s.stackedCandidates,
  );
  const selectionCycling = useSysvarStore((s) => s.values.SELECTIONCYCLING);
  const visible = selectionCycling !== 0 && stackedCandidates.length >= 2;
  return (
    <SelectionCyclingBadge
      cursorClient={cursorClient}
      visible={visible}
      count={stackedCandidates.length}
    />
  );
}

/**
 * B12 — listbox dialog that appears when SELECTIONCYCLING bit 2 is set
 * and the user clicks a stacked region. Pick → reducer dispatches a
 * single-id `shell/select-entities` then closes the dialog.
 */
function SelectionCyclingDialogMount(): ReactElement | null {
  const dialog = useSelectionCyclingStore((s) => s.dialog);
  const closeDialog = useSelectionCyclingStore((s) => s.closeDialog);
  const dispatch = useEditorStore((s) => s.dispatch);
  if (!dialog) return null;
  return (
    <Suspense fallback={null}>
      <SelectionCyclingDialog
        screenPos={dialog.screen}
        candidates={dialog.candidates}
        onPick={(entityId) => {
          dispatch({
            type: "shell/select-entities",
            source: "scene",
            entityIds: [entityId],
          });
          closeDialog();
        }}
        onClose={closeDialog}
      />
    </Suspense>
  );
}
